import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  Image,
  Modal,
  TextInput,
} from "react-native";
import * as ExpoLocation from "expo-location";
import * as DocumentPicker from "expo-document-picker";
import * as ImagePicker from "expo-image-picker";
import type { NavigationProp } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Briefcase,
  Building2,
  Calculator,
  Camera,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  CreditCard,
  FileCheck2,
  FileQuestion,
  FileText,
  Gift,
  Landmark,
  MapPin,
  MessageCircle,
  Minus,
  Plus,
  Receipt,
  Shield,
  SlidersHorizontal,
  Sparkles,
  UploadCloud,
  Wallet,
  X,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  projectDocumentProcessingRunToPrefill,
  useDocumentSourceTransfer,
} from "@src/packages/documents/ingestion";
import { resolveUploadContentType } from "@src/packages/utils/fileUpload";
import {
  useClinicQuotes,
  useCreateClinicQuote,
  useDeleteClinicQuote,
  useUpdateClinicQuote,
  type ApiClinicQuote,
  type ClinicQuoteExtraction,
} from "@src/packages/costs/hooks/useClinicQuotes";
import {
  useCreatePaymentRecord,
  useDeletePaymentRecord,
  usePaymentRecords,
  useUpdatePaymentRecord,
  type CreatePaymentRecordPayload,
} from "@src/packages/costs/hooks/usePaymentRecords";
import { useClinicComparisonStore } from "@src/packages/clinics/clinicComparisonStore";
import { useCoverage } from "@src/packages/coverage/hooks/useCoverage";
import type {
  FertilityCoverage,
  InsurancePolicy,
} from "@src/packages/coverage/hooks/useCoverage";
import { useCoverageFinancials } from "@src/packages/coverage/hooks/useCoverageFinancials";
import {
  buildEstimateScenarioSections,
  buildDetectedEstimateAreaOption,
  buildFallbackEstimateAreaOption,
  buildProfileEstimateAreaOption,
  getUsStateName,
  getUsStateCentroid,
  hasTransferPath,
  hasMedicationEstimate,
  inferUsStateCodeFromZip,
  isLocationSpecificEstimate,
  normalizeUsStateCode,
  resolvePlaceEstimateArea,
  US_CITY_ESTIMATE_AREA_OPTIONS,
  type PlaceEstimateInput,
  type PlaceEstimateResolution,
  type EstimateAreaOption,
  type EstimateLineStatus,
  type EstimateScenarioLine,
  type EstimateScenarioSection,
} from "@src/packages/costs/estimates";
import {
  getPlaceDetails,
  extractPlaceArea,
} from "@src/packages/clinics/utils/googlePlaces";
import { usePlacesPredictions } from "@src/packages/hooks/usePlacesPredictions";
import {
  type DiagnosticProfile,
  type TreatmentType,
} from "@src/packages/homepage/utils/costEstimator";
import {
  formatCurrency,
  formatDate,
  type Locale,
  RegionGate,
  useLocale,
  useRegion,
  useTranslations,
} from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { resolvePlannedModality } from "@src/packages/homepage/plan/resolvePlannedModality";
import type { AcceptedTreatmentPlan } from "@src/packages/homepage/plan/treatmentPlanTypes";
import { normalizePlannedModalities } from "../../access/findCareIntent";
import { useAuthorizedExperienceProjection } from "@src/packages/experience/hooks/useAuthorizedExperienceProjection";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  SectionTitle,
  GradientIcon,
  MoonStepper,
  Collapsible,
  Dialog,
  DialogActions,
  DialogHeader,
  DialogPrimaryButton,
  Row,
  CardSubtitleText,
  SoftSecondaryButton,
} from "@louise/moon-ui";
import type { GradientVariant } from "@louise/moon-ui/primitives/GradientIcon";
import {
  GLASS_CARD_STYLE,
  HEADER_INSET,
  SCREEN_PADDING_TOP,
  colors,
} from "@louise/moon-ui/theme/tokens";
import { EMBER } from "../../../../onboarding/theme";
import {
  apiToUiPaymentRecord,
  parseDateText,
  todayIsoDate,
  uiToPaymentRecordUpdates,
} from "../paymentRecordMapping";
import { summarizePaymentRecords } from "../paymentRecordSummary";
import {
  hasCostDocumentUpload,
  mergeCostDocumentReferences,
  toAttachedCostDocumentReference,
  type FiledCostDocument,
} from "@src/packages/costs/document-linkage";
import {
  currentFinancingProviders,
  hasCurrentPromotionalFinancing,
} from "@src/packages/costs/financialContent";
import { useOnboarding } from "../../../../onboarding/OnboardingContext";
import type { TreatmentIntent } from "../../../../onboarding/types";
import type {
  CostPaymentLineItemParam,
  CostPaymentRecordKind,
  CostPaymentRecordParam,
  CostPaymentsRouteParams,
  CostsStackParamList,
  MainTabParamList,
} from "../../../types";
import InfoSheet, { InfoButton } from "../../../components/InfoSheet";
import PillSelect from "../../../components/PillSelect";
import { useEstimateLocationIntent } from "../../../hooks/useEstimateLocationIntent";
import { useFlowAnalytics } from "@src/packages/analytics/useFlowAnalytics";
import ScreenHeader from "../../../components/ScreenHeader";
import StatusBadge from "../../../components/StatusBadge";
import ViewAllLink from "../../../components/ViewAllLink";
import CostsCardTabs from "../../../components/CostsCardTabs";
import { CarePillActionButton } from "@src/packages/homepage/plan/components/CarePillActionButton";
import InsuranceInfoModal from "../../YouScreen/modals/InsuranceInfoModal";
import ChatWithUsCard from "../../CostsScreen/ChatWithUsCard";
import CompareClinicCostsCard from "../../CostsScreen/CompareClinicCostsCard";
import FaqSection from "../../CostsScreen/FaqSection";
import InsuranceGlossaryCard from "../../CostsScreen/InsuranceGlossaryCard";
import { GRANTS } from "../grants.data";
import PaymentRecordDetailModal, {
  buildPaymentRecordChatPrompt,
} from "../PaymentRecordDetailModal";

const COST_ESTIMATE_ILLUSTRATION = require("../../../../assets/images/cost-estimate-coins.png");
const COST_QUOTE_RECEIPT_ILLUSTRATION = require("../../../../assets/images/cost-quote-receipt.png");
const ORGANIC_BAR_COLORS = {
  paid: "#F4B34F",
  claims: "#5B332C",
  left: "#B76545",
} as const;
const PAYMENT_ROW_ICON_COLOR = colors.greyBrown;
const PAYMENT_RECORD_PREVIEW_LIMIT = 4;

type Props = NativeStackScreenProps<CostsStackParamList, "CostsOverview">;

type ConceptCostState = "estimate" | "clinic-estimate" | "payments";
type StatusBadgeTone =
  "neutral" | "gold" | "coral" | "green" | "orange" | "blue" | "purple";
type CostsOverviewRouteName = Extract<
  Exclude<keyof CostsStackParamList, "CostsOverview" | "GrantDetail">,
  string
>;
type CostsOverviewRouteParams = { paymentRecordId?: string };

type CategoryCard = {
  title: string;
  subtitle: string;
  icon: LucideIcon;
  iconVariant: GradientVariant;
  route: CostsOverviewRouteName;
  /** i18n key of `title`, kept stable for analytics across locales. */
  action: string;
};

type CostStateSummary = {
  eyebrow: string;
  title: string;
  amount: string;
  amountLabel: string;
  description: string;
  badge: string;
  badgeTone: "estimate" | "quote" | "payments";
  rows: { label: string; value: string; detail?: string }[];
  primaryAction: string;
  secondaryAction: string;
  primaryRoute?: CostsOverviewRouteName;
  secondaryRoute?: CostsOverviewRouteName;
};

export type CostUploadMode = "quote" | "payment";
export type ManualPaymentKind = "payment" | "claim" | "receipt" | "balance";

type QuoteOCRResult = {
  clinicName: string | null;
  quoteDate: string | null;
  currency: string | null;
  totalAmount: string | null;
  lineItems: {
    label: string | null;
    amount: string | null;
    category: string | null;
    notes: string | null;
  }[];
};

export type UploadedCostDocument = {
  id: string;
  mode: CostUploadMode;
  source: "camera" | "document";
  name: string;
  uri: string;
  mimeType: string;
  paymentKind?: ManualPaymentKind;
  /** S3 uploadId once the file is filed — used to link the canonical source. */
  uploadId?: string | null;
  /** Canonical Documents record ID once the file is filed. */
  documentId?: string | null;
};

export type ManualPaymentEntry = {
  id: string;
  kind: ManualPaymentKind;
  label: string;
  provider: string;
  amount: number;
  date: string;
  note: string;
  attachmentName?: string;
  attachmentUploadId?: string | null;
  attachmentDocumentId?: string | null;
};

type PendingCostUpload = Pick<
  UploadedCostDocument,
  "source" | "name" | "uri" | "mimeType"
> & {
  docName: string;
  tags: string[];
  uploadId?: string;
};

type QuoteAnalysisStatus = "ready" | "available" | "needs-upload";

type QuoteLineItem = {
  key: string;
  label: string;
  amount: number;
  category: string | null;
  status: string;
  coverage: string;
  financing: string;
  detail: string;
};

type ClinicQuote = {
  id: string;
  clinic: string;
  location: string;
  logoText: string;
  status: string;
  statusTone: "ready" | "pending" | "attention";
  quoteTotal: number;
  outOfPocket: number;
  uploadedAt: string;
  documentLabel: string;
  quoteDate: string | null;
  summary: string;
  analysisStatus: QuoteAnalysisStatus;
  analysisTitle: string;
  analysisBody: string;
  missingItems: string[];
  lineItems: QuoteLineItem[];
};

export type QuoteComparisonContext = {
  quotes: ClinicQuote[];
  treatmentLabel: string;
  careLocation: string;
  hasInsurance: boolean;
  verificationStatus: "unverified" | "pending" | "verified";
  primaryPolicy: InsurancePolicy | null;
  fertilityCoverage: FertilityCoverage | null;
};

function formatChatContext(value: unknown) {
  if (value == null) return "not available";
  return JSON.stringify(value, null, 2) ?? "not available";
}

export function buildQuoteComparisonChatPrompt(
  context: QuoteComparisonContext,
  locale: Locale,
) {
  const quoteSections = context.quotes.map((quote, index) => {
    const lineItems = quote.lineItems.length
      ? quote.lineItems
          .map(
            (line) =>
              `  - ${line.label}: ${formatCurrency(line.amount, locale)} · ${line.status} · ${line.coverage} · ${line.financing} · ${line.detail}`,
          )
          .join("\n")
      : "  - No readable line items";

    return [
      `Quote ${index + 1}`,
      `- Clinic: ${quote.clinic}`,
      `- Location: ${quote.location}`,
      `- Document: ${quote.documentLabel}`,
      `- Status: ${quote.status}`,
      `- Quote total: ${formatCurrency(quote.quoteTotal, locale)}`,
      `- Estimated out-of-pocket: ${formatCurrency(quote.outOfPocket, locale)}`,
      `- Summary: ${quote.summary}`,
      `- Analysis: ${quote.analysisTitle} — ${quote.analysisBody}`,
      `- Missing items: ${quote.missingItems.join(", ") || "none listed"}`,
      "- Line items:",
      lineItems,
    ].join("\n");
  });

  return [
    "Compare the uploaded fertility clinic quotes for me and help me decide what to ask each clinic next.",
    "Use every quote and insurance detail below. Compare total charges, included and missing items, estimated patient responsibility, billing entities, exclusions, and any uncertainty. Do not assume that a quote is covered just because a benefit exists; call out what must be verified and distinguish document facts from estimates.",
    "Comparison context:",
    `- Treatment path: ${context.treatmentLabel}`,
    `- Care location: ${context.careLocation}`,
    "Insurance details:",
    `- Insurance added: ${context.hasInsurance ? "yes" : "no"}`,
    `- Verification status: ${context.verificationStatus}`,
    "- Policy details:",
    formatChatContext(context.primaryPolicy),
    "- Fertility coverage details:",
    formatChatContext(context.fertilityCoverage),
    "Uploaded quotes:",
    quoteSections.join("\n\n") || "No quotes are available yet.",
    "Please give me a side-by-side comparison, identify the best follow-up questions for each clinic, and explain which missing insurance or billing details could change the ranking.",
  ].join("\n");
}

type PaymentRecordStatus =
  | "paid"
  | "submitted"
  | "waiting-eob"
  | "needs-receipt"
  | "partially-covered"
  | "ready-reimbursement"
  | "denied"
  | "due"
  | "review";

type PaymentSplitLine = {
  id: string;
  label: string;
  amount: number;
  detail: string;
  color: string;
};

type PaymentDocumentLine = {
  id: string;
  label: string;
  detail: string;
  status: "attached" | "needed" | "pending";
  uploadId?: string | null;
};

type PaymentRecord = {
  id: string;
  kind?: CostPaymentRecordKind;
  label: string;
  source: string;
  provider: string;
  phase: string;
  amount: number;
  billedAmount: number;
  status: PaymentRecordStatus;
  date: string;
  headline: string;
  detail: string;
  coverage: string;
  financing: string;
  nextAction: string;
  splitLines: PaymentSplitLine[];
  lineItems?: CostPaymentLineItemParam[];
  documents: PaymentDocumentLine[];
};

type PaymentBreakdownSegment = {
  label: string;
  value: number;
  detail: string;
  color: string;
};

const TREATMENT_LABELS: Record<TreatmentType, string> = {
  egg_freezing: "Egg freezing",
  embryo_freezing: "Embryo freezing",
  sperm_freezing: "Sperm freezing",
  ivf: "IVF",
  ivf_icsi: "IVF + ICSI",
  fet: "Frozen embryo transfer",
  iui: "IUI",
  ovulation_induction: "Ovulation induction",
};

const CONCEPT_TREATMENT_OPTIONS: TreatmentType[] = [
  "egg_freezing",
  "ivf",
  "ivf_icsi",
  "iui",
  "fet",
  "embryo_freezing",
  "sperm_freezing",
  "ovulation_induction",
];

const ESTIMATE_INFO_SECTIONS = [
  {
    title: "What changes the estimate",
    body: "Treatment, location, and insurance status drive the estimate. Treatment controls the base clinical path. Location changes the market-rate assumption. Insurance becomes plan-aware after benefits are verified.",
  },
  {
    title: "What is inside",
    body: "The estimate can include the cycle fee, medications, diagnostics, one year of storage, and treatment-specific add-ons when those assumptions are selected. Public averages are not a clinic quote.",
  },
  {
    title: "Clinic quotes",
    body: "Upload an itemized quote to compare included and missing line items, bundled fees, and whether verified benefits may make one clinic less expensive than another.",
  },
  {
    title: "Payments and claims",
    body: "After treatment starts, track deposits, clinic balances, insurance claims, EOBs, medication receipts, and expected remaining responsibility.",
  },
  {
    title: "Location and logistics",
    body: "Lower sticker prices can come with added monitoring, medication timing, clinic coordination, legal, travel, and continuity-of-care tradeoffs. This is guidance, not a recommendation.",
  },
];

type ConceptLocationOption = EstimateAreaOption & {
  action?: "request-current-location";
};

function getAcceptedPlanTreatmentType(
  treatmentFamily: AcceptedTreatmentPlan["treatmentFamily"] | null | undefined,
): TreatmentType | null {
  switch (treatmentFamily) {
    case "ivf":
      return "ivf";
    case "iui":
      return "iui";
    case "egg_freezing":
      return "egg_freezing";
    case "embryo_freezing":
      return "embryo_freezing";
    case "sperm_freezing":
      return "sperm_freezing";
    case "fet":
      return "fet";
    case "oi":
      return "ovulation_induction";
    default:
      return null;
  }
}

function getCanonicalTreatmentType(
  treatmentGoal: string | null | undefined,
  plannedModality: "ivf" | "iui" | "fet" | null,
): TreatmentType | null {
  if (plannedModality) return plannedModality;

  switch (treatmentGoal) {
    case "ivf":
      return "ivf";
    case "iui":
      return "iui";
    case "fet":
      return "fet";
    case "egg-freezing":
      return "egg_freezing";
    case "embryo-freezing":
      return "embryo_freezing";
    case "sperm-freezing":
      return "sperm_freezing";
    default:
      // Broad pregnancy, preservation, and exploration intents do not
      // authorize a treatment-specific estimate without an explicit path.
      return null;
  }
}
function getUrlCostState(): ConceptCostState | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    return normalizeConceptCostState(
      params.get("dev_costs_state") ??
        params.get("dev_costs_suggested") ??
        params.get("dev_costs_surface"),
    );
  } catch {
    return null;
  }
}

export function getDevSuggestedCostsSurface(): string | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("dev_costs_state") ??
      params.get("dev_costs_suggested") ??
      params.get("dev_costs_surface")
    );
  } catch {
    return null;
  }
}

// Dev-only fixture that replays a resolved location on mount so the
// arbitrary-location states render deterministically for visual evidence,
// without a live Places call (CI has no Maps key). Formats:
//   ?dev_costs_location=Tucson, AZ        US city -> nearest-metro fallback
//   ?dev_costs_location=Lyon|FR|France    non-US city with country-only cover
//   ?dev_costs_location=Toronto|CA|Canada country with no coverage
function getDevCostsLocationFixture(): PlaceEstimateInput | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  let raw: string | null = null;
  try {
    raw = new URLSearchParams(window.location.search).get("dev_costs_location");
  } catch {
    return null;
  }
  const value = raw?.trim();
  if (!value) return null;

  if (value.includes("|")) {
    const [cityPart, countryCode, countryName] = value
      .split("|")
      .map((part) => part.trim());
    return {
      granularity: "city",
      label: cityPart || countryName || countryCode || value,
      city: cityPart || null,
      countryCode: countryCode || "US",
      countryName: countryName || null,
    };
  }

  const parts = value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const city = parts[0] ?? value;
  const stateCode = parts[1] ?? null;
  return {
    granularity: "city",
    label: stateCode ? `${city}, ${stateCode}` : city,
    city,
    stateCode,
    countryCode: "US",
    countryName: "United States",
  };
}

export function getDevPaymentRecordFixtures(): CostPaymentRecordParam[] | null {
  if (!__DEV__ || typeof window === "undefined") return null;

  let state: string | null = null;
  try {
    const params = new URLSearchParams(window.location.search);
    state = params.get("dev_costs_payment_record");
  } catch {
    return null;
  }

  const deniedClaim: CostPaymentRecordParam = {
    id: "dev-denied-claim",
    kind: "claim",
    label: "IVF cycle claim",
    source: "Insurance claim",
    provider: "Northstar Health",
    phase: "Post-cycle",
    amount: 2400,
    billedAmount: 3000,
    status: "denied",
    date: "Jun 18, 2026",
    headline: "$2,400 denied",
    detail: "Claim denied after coverage review.",
    coverage: "Denied by plan",
    financing: "Not financed yet",
    nextAction: "Review insurance",
    splitLines: [
      {
        id: "dev-denied-claim-insurance",
        label: "Insurance denied",
        amount: 2400,
        detail: "Coverage review required",
        color: ORGANIC_BAR_COLORS.claims,
      },
      {
        id: "dev-denied-claim-patient",
        label: "Patient responsibility",
        amount: 600,
        detail: "Expected balance",
        color: ORGANIC_BAR_COLORS.left,
      },
    ],
    lineItems: [
      {
        id: "dev-denied-claim-line-item",
        label: "IVF cycle",
        amount: 3000,
        detail: "Cycle treatment claim",
        allocations: [
          {
            id: "dev-denied-claim-allocation-insurance",
            kind: "insurance",
            label: "Insurance",
            amount: 2400,
          },
          {
            id: "dev-denied-claim-allocation-patient",
            kind: "patient",
            label: "You",
            amount: 600,
          },
        ],
      },
    ],
    documents: [],
  };

  if (state === "denied") return [deniedClaim];
  if (state !== "filled") return null;

  return [
    {
      ...deniedClaim,
      id: "dev-filled-payment",
      kind: "payment",
      label: "Clinic deposit",
      source: "Clinic payment",
      provider: "Northstar Health",
      phase: "Before cycle",
      amount: 1200,
      billedAmount: 1200,
      status: "paid",
      date: "Jun 12, 2026",
      headline: "$1,200 recorded",
      detail: "Deposit recorded against the clinic plan.",
      coverage: "Save for reimbursement review",
      nextAction: "Add document",
      splitLines: [
        {
          id: "dev-filled-payment-you",
          label: "Paid by you",
          amount: 1200,
          detail: "Clinic deposit",
          color: ORGANIC_BAR_COLORS.paid,
        },
      ],
      lineItems: [],
    },
    deniedClaim,
    {
      ...deniedClaim,
      id: "dev-filled-receipt",
      kind: "receipt",
      label: "Medication receipt",
      source: "Pharmacy receipt",
      provider: "Harbor Pharmacy",
      phase: "Medication",
      amount: 380,
      billedAmount: 380,
      status: "needs-receipt",
      date: "Jun 8, 2026",
      headline: "$380 receipt",
      detail: "Medication receipt awaiting document match.",
      coverage: "Confirm coverage path",
      nextAction: "Add document",
      splitLines: [
        {
          id: "dev-filled-receipt-you",
          label: "Paid by you",
          amount: 380,
          detail: "Medication",
          color: ORGANIC_BAR_COLORS.paid,
        },
      ],
      lineItems: [],
      documents: [
        {
          id: "dev-filled-receipt-needed",
          label: "Supporting document",
          detail: "Add the pharmacy receipt when available",
          status: "needed",
        },
      ],
    },
    {
      ...deniedClaim,
      id: "dev-filled-balance",
      kind: "balance",
      label: "Remaining clinic balance",
      source: "Clinic statement",
      provider: "Northstar Health",
      phase: "Before cycle",
      amount: 650,
      billedAmount: 650,
      status: "due",
      date: "Jun 20, 2026",
      headline: "$650 left to pay",
      detail: "Balance from the latest clinic statement.",
      coverage: "Confirm coverage path",
      financing: "Can plan now",
      nextAction: "Review document",
      splitLines: [
        {
          id: "dev-filled-balance-patient",
          label: "Patient responsibility",
          amount: 650,
          detail: "Remaining balance",
          color: ORGANIC_BAR_COLORS.left,
        },
      ],
      lineItems: [],
    },
  ];
}

function getDevClinicQuoteFixtures(): ApiClinicQuote[] {
  if (!__DEV__ || typeof window === "undefined") return [];

  let state: string | null = null;
  try {
    const params = new URLSearchParams(window.location.search);
    state = params.get("dev_costs_quote_state");
  } catch {
    return [];
  }

  if (state !== "error" && state !== "filled") return [];

  const now = new Date().toISOString();
  if (state === "error") {
    return [
      {
        id: "dev-unreadable-clinic-quote",
        patientId: "dev-patient",
        name: "Unreadable clinic quote.pdf",
        uploadId: null,
        mimeType: "application/pdf",
        extraction: null,
        source: "patient",
        verificationStatus: "unverified",
        verifiedAt: null,
        verifiedBy: null,
        verificationNotes: null,
        createdAt: now,
        updatedAt: now,
        updatedBy: null,
      },
    ];
  }

  return [
    {
      id: "dev-filled-clinic-quote-a",
      patientId: "dev-patient",
      name: "Northstar Fertility quote.pdf",
      uploadId: null,
      mimeType: "application/pdf",
      extraction: {
        clinicName: "Northstar Fertility",
        quoteDate: "2026-06-18",
        currency: "USD",
        totalAmount: "14800",
        lineItems: [
          {
            label: "IVF cycle package",
            amount: "9800",
            category: "Treatment",
            notes: "Includes monitoring and retrieval.",
          },
          {
            label: "Embryology and lab fees",
            amount: "3200",
            category: "Laboratory",
            notes: "Includes fertilization and embryo culture.",
          },
          {
            label: "Anesthesia",
            amount: "1800",
            category: "Procedure",
            notes: "Billed by a separate anesthesia group.",
          },
        ],
      },
      source: "patient",
      verificationStatus: "unverified",
      verifiedAt: null,
      verifiedBy: null,
      verificationNotes: null,
      createdAt: now,
      updatedAt: now,
      updatedBy: null,
    },
    {
      id: "dev-filled-clinic-quote-b",
      patientId: "dev-patient",
      name: "Harbor Reproductive Care quote.pdf",
      uploadId: null,
      mimeType: "application/pdf",
      extraction: {
        clinicName: "Harbor Reproductive Care",
        quoteDate: "2026-06-21",
        currency: "USD",
        totalAmount: "16250",
        lineItems: [
          {
            label: "IVF cycle package",
            amount: "11250",
            category: "Treatment",
            notes: "Includes monitoring, retrieval, and transfer planning.",
          },
          {
            label: "Embryology and lab fees",
            amount: "3200",
            category: "Laboratory",
            notes: "ICSI is optional and not included.",
          },
          {
            label: "Anesthesia and facility fee",
            amount: "1800",
            category: "Procedure",
            notes: "Facility fee is included; anesthesia provider to confirm.",
          },
        ],
      },
      source: "patient",
      verificationStatus: "unverified",
      verifiedAt: null,
      verifiedBy: null,
      verificationNotes: null,
      createdAt: now,
      updatedAt: now,
      updatedBy: null,
    },
  ];
}

export function normalizeConceptCostState(
  raw: string | null | undefined,
): ConceptCostState | null {
  if (raw === "estimate" || raw === "exploring") return "estimate";
  if (
    raw === "quote" ||
    raw === "quotes" ||
    raw === "clinic" ||
    raw === "clinic-estimate"
  ) {
    return "clinic-estimate";
  }
  if (raw === "payments" || raw === "claims" || raw === "active") {
    return "payments";
  }
  return null;
}

function deriveConceptCostState(params: {
  intent: TreatmentIntent | null;
  comparisonCount: number;
  verifiedClinicCount: number;
}): ConceptCostState {
  const normalizedIntent = params.intent?.toLowerCase();
  if (
    normalizedIntent === "intreatment" ||
    normalizedIntent === "startingtreatment"
  ) {
    return "payments";
  }

  if (params.verifiedClinicCount > 0 || params.comparisonCount > 0) {
    return "clinic-estimate";
  }

  return "estimate";
}

function estimatePatientResponsibility(
  totalCost: number,
  fertilityCoverage: FertilityCoverage | null,
): number | null {
  if (!fertilityCoverage) return null;

  const deductible = fertilityCoverage.deductibleCents
    ? fertilityCoverage.deductibleCents / 100
    : 0;
  const coinsurance =
    fertilityCoverage.coinsurancePercent != null
      ? fertilityCoverage.coinsurancePercent / 100
      : 0.2;
  const oopMax =
    fertilityCoverage.outOfPocketMaxCents != null
      ? fertilityCoverage.outOfPocketMaxCents / 100
      : Number.POSITIVE_INFINITY;

  const afterDeductible = Math.max(totalCost - deductible, 0);
  const patientShare =
    totalCost <= deductible
      ? totalCost
      : deductible + afterDeductible * coinsurance;

  return Math.min(patientShare, oopMax);
}

export function CostDocumentUploadModal({
  visible,
  mode,
  documentOnly = false,
  onClose,
  onDocumentPicked,
  onManualPaymentAdded,
  initialPaymentKind,
}: {
  visible: boolean;
  mode: CostUploadMode;
  documentOnly?: boolean;
  onClose: () => void;
  onDocumentPicked: (document: UploadedCostDocument) => void | Promise<void>;
  onManualPaymentAdded: (entry: ManualPaymentEntry) => void | Promise<void>;
  initialPaymentKind?: ManualPaymentKind;
}) {
  const capturePress = usePressCapture("CostDocumentUploadModal");
  const [busySource, setBusySource] = useState<
    UploadedCostDocument["source"] | null
  >(null);
  const [paymentKind, setPaymentKind] = useState<ManualPaymentKind>(
    initialPaymentKind ?? "payment",
  );
  const [paymentLabel, setPaymentLabel] = useState("");
  const [paymentProvider, setPaymentProvider] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [paymentAttachmentName, setPaymentAttachmentName] = useState<
    string | null
  >(null);
  const [paymentAttachmentUri, setPaymentAttachmentUri] = useState<
    string | null
  >(null);
  const [paymentAttachmentMimeType, setPaymentAttachmentMimeType] = useState(
    "application/octet-stream",
  );
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [pendingDocument, setPendingDocument] =
    useState<UploadedCostDocument | null>(null);
  const [pendingUpload, setPendingUpload] = useState<PendingCostUpload | null>(
    null,
  );
  const [pendingPaymentAttachment, setPendingPaymentAttachment] =
    useState<UploadedCostDocument | null>(null);

  const transfer = useDocumentSourceTransfer();

  useEffect(() => {
    if (visible && mode === "payment" && initialPaymentKind) {
      setPaymentKind(initialPaymentKind);
    }
  }, [initialPaymentKind, mode, visible]);

  // Tag derivation: route cost uploads into the central Documents folder so
  // they show up alongside other records. Manual-payment attachments use the
  // payment kind to land in the closest matching slot/category.
  const tagsForQuote = (): string[] => ["coverage_benefits"];
  const tagsForPaymentMode = (): string[] => ["clinic-invoices", "admin"];
  const tagsForPaymentKind = (kind: ManualPaymentKind): string[] => {
    switch (kind) {
      case "claim":
        return ["eob", "coverage_benefits"];
      case "receipt":
        return ["pharmacy-receipts", "admin"];
      case "payment":
      case "balance":
      default:
        return ["clinic-invoices", "admin"];
    }
  };

  // Upload a durable source and wait for its canonical processing run. The
  // pending upload keeps the completed upload ID when processing fails so a
  // retry can resume without uploading the file again.
  const uploadAndFileDocument = async (
    file: {
      uri: string;
      name: string;
      mimeType: string;
      uploadId?: string | null;
    },
    docName: string,
    tags: string[],
  ): Promise<FiledCostDocument | null> => {
    setUploadError(null);
    try {
      const source = file.uploadId
        ? { file, uploadId: file.uploadId }
        : await transfer.uploadSource(file, {
            destination: "document",
            sourceContext: {
              surface: "cost_document_upload_modal",
              form: mode,
              targetHints: tags,
              allowBackgroundProcessing: true,
            },
          });
      setPendingUpload((pending) =>
        pending ? { ...pending, uploadId: source.uploadId } : pending,
      );
      const outcome = await transfer.processSource(source, {
        attempts: 24,
        intervalMs: 1_250,
      });
      return {
        documentId: outcome.patientDocumentId,
        uploadId: source.uploadId,
        name: file.name,
      };
    } catch (uploadError) {
      console.error("Failed to upload cost document:", uploadError);
      setUploadError(
        "We couldn't save that file. Check your connection and try again.",
      );
      return null;
    }
  };

  if (!visible) return null;

  const resetPaymentDraft = () => {
    setPaymentKind("payment");
    setPaymentLabel("");
    setPaymentProvider("");
    setPaymentAmount("");
    setPaymentDate("");
    setPaymentNote("");
    setPaymentAttachmentName(null);
    setPaymentAttachmentUri(null);
    setPaymentAttachmentMimeType("application/octet-stream");
    setPendingDocument(null);
    setPendingUpload(null);
    setPendingPaymentAttachment(null);
  };

  const handleClose = () => {
    resetPaymentDraft();
    setUploadError(null);
    onClose();
  };

  const complete = async (
    source: UploadedCostDocument["source"],
    name: string,
    uri: string,
    mimeType: string,
  ) => {
    const docName =
      mode === "quote"
        ? `Clinic quote – ${name}`
        : `Payment document – ${name}`;
    const tags = mode === "quote" ? tagsForQuote() : tagsForPaymentMode();
    const upload: PendingCostUpload = {
      source,
      name,
      uri,
      mimeType,
      docName,
      tags,
    };
    setPendingUpload(upload);
    const filedDocument = await uploadAndFileDocument(
      { uri, name, mimeType },
      docName,
      tags,
    );
    if (!filedDocument) return;
    setPendingUpload(null);

    const uploadedDocument: UploadedCostDocument = {
      id: `${mode}-${Date.now()}`,
      mode,
      source,
      name,
      uri,
      mimeType,
      paymentKind: mode === "payment" ? paymentKind : undefined,
      uploadId: filedDocument.uploadId,
      documentId: filedDocument.documentId,
    };
    setPendingDocument(uploadedDocument);

    try {
      await onDocumentPicked(uploadedDocument);
      setPendingDocument(null);
      handleClose();
    } catch (error) {
      console.error("Failed to save uploaded cost record:", error);
      setUploadError(
        "We couldn't attach that file to the record. Check your connection and try again.",
      );
    }
  };

  const retryPendingDocument = async () => {
    if (!pendingDocument) return;
    setUploadError(null);
    try {
      await onDocumentPicked(pendingDocument);
      setPendingDocument(null);
      handleClose();
    } catch (error) {
      console.error("Failed to save uploaded cost record:", error);
      setUploadError(
        "We couldn't attach that file to the record. Check your connection and try again.",
      );
    }
  };

  const retryPendingUpload = async () => {
    if (!pendingUpload) return;
    setUploadError(null);
    try {
      const filedDocument = await uploadAndFileDocument(
        pendingUpload,
        pendingUpload.docName,
        pendingUpload.tags,
      );
      if (!filedDocument) return;
      setPendingUpload(null);
      const uploadedDocument: UploadedCostDocument = {
        id: `${mode}-${Date.now()}`,
        mode,
        source: pendingUpload.source,
        name: pendingUpload.name,
        uri: pendingUpload.uri,
        mimeType: pendingUpload.mimeType,
        paymentKind: mode === "payment" ? paymentKind : undefined,
        uploadId: filedDocument.uploadId,
        documentId: filedDocument.documentId,
      };
      setPendingDocument(uploadedDocument);
      await onDocumentPicked(uploadedDocument);
      setPendingDocument(null);
      handleClose();
    } catch (error) {
      console.error("Failed to retry cost document upload:", error);
      setUploadError(
        "We couldn't save that file. Check your connection and try again.",
      );
    }
  };

  const pickPaymentAttachment = async () => {
    setBusySource("document");
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets?.[0]) {
        const file = result.assets[0];
        setUploadError(null);
        setPaymentAttachmentName(file.name);
        setPaymentAttachmentUri(file.uri);
        setPaymentAttachmentMimeType(
          resolveUploadContentType({
            name: file.name,
            uri: file.uri,
            mimeType: file.mimeType,
          }),
        );
        setPendingPaymentAttachment(null);
      }
    } finally {
      setBusySource(null);
    }
  };

  const parsedPaymentAmount = Number(paymentAmount.replace(/[^0-9.]/g, ""));
  // A ledger entry needs at least a real amount or an attached document —
  // otherwise "Add record" would file an empty $0 placeholder with no source.
  const canSaveManualPayment =
    (Number.isFinite(parsedPaymentAmount) && parsedPaymentAmount > 0) ||
    paymentAttachmentName != null;

  const saveManualPayment = async () => {
    if (!canSaveManualPayment) return;
    const numericAmount = parsedPaymentAmount;
    const defaultLabel: Record<ManualPaymentKind, string> = {
      payment: "Payment record",
      claim: "Insurance claim",
      receipt: "Receipt",
      balance: "Remaining balance",
    };

    try {
      let filedAttachment: FiledCostDocument | null = pendingPaymentAttachment
        ? pendingPaymentAttachment.documentId &&
          pendingPaymentAttachment.uploadId
          ? {
              documentId: pendingPaymentAttachment.documentId ?? "",
              uploadId: pendingPaymentAttachment.uploadId ?? "",
              name: pendingPaymentAttachment.name,
            }
          : null
        : null;
      if (paymentAttachmentUri && paymentAttachmentName) {
        if (!filedAttachment?.documentId || !filedAttachment.uploadId) {
          const pendingAttachment: UploadedCostDocument = {
            id: pendingPaymentAttachment?.id ?? `payment-${Date.now()}`,
            mode: "payment",
            source: "document",
            name: paymentAttachmentName,
            uri: paymentAttachmentUri,
            mimeType: paymentAttachmentMimeType,
            paymentKind,
          };
          setPendingPaymentAttachment(pendingAttachment);
          setBusySource("document");
          filedAttachment = await uploadAndFileDocument(
            pendingAttachment,
            `Payment document – ${paymentAttachmentName}`,
            tagsForPaymentKind(paymentKind),
          );
          setBusySource(null);
          if (!filedAttachment) return;
          setPendingPaymentAttachment({
            ...pendingAttachment,
            uploadId: filedAttachment.uploadId,
            documentId: filedAttachment.documentId,
          });
        }
      }
      await onManualPaymentAdded({
        id: `manual-payment-${Date.now()}`,
        kind: paymentKind,
        label: paymentLabel.trim() || defaultLabel[paymentKind],
        provider: paymentProvider.trim() || "Provider not set",
        amount: Number.isFinite(numericAmount) ? numericAmount : 0,
        date: paymentDate.trim() || "Added today",
        note: paymentNote.trim(),
        attachmentName: paymentAttachmentName ?? undefined,
        attachmentUploadId: filedAttachment?.uploadId ?? null,
        attachmentDocumentId: filedAttachment?.documentId ?? null,
      });
      setPendingPaymentAttachment(null);
      handleClose();
    } catch (error) {
      console.error("Failed to save manual payment record:", error);
      setUploadError(
        "We couldn't add that record. Check your connection and try again.",
      );
    }
  };

  const handleCamera = async () => {
    setBusySource("camera");
    try {
      const permission = await ImagePicker.requestCameraPermissionsAsync();
      if (!permission.granted) return;

      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ["images"],
        quality: 0.82,
      });

      if (!result.canceled && result.assets?.[0]) {
        const asset = result.assets[0];
        await complete(
          "camera",
          `${mode}-photo-${Date.now()}.jpg`,
          asset.uri,
          resolveUploadContentType({
            name: `${mode}-photo.jpg`,
            uri: asset.uri,
            mimeType: asset.mimeType,
          }),
        );
      }
    } finally {
      setBusySource(null);
    }
  };

  const handleDocument = async () => {
    setBusySource("document");
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets?.[0]) {
        const file = result.assets[0];
        await complete(
          "document",
          file.name,
          file.uri,
          resolveUploadContentType({
            name: file.name,
            uri: file.uri,
            mimeType: file.mimeType,
          }),
        );
      }
    } finally {
      setBusySource(null);
    }
  };

  const renderPaymentInput = (
    label: string,
    value: string,
    onChangeText: (value: string) => void,
    placeholder: string,
    options: {
      keyboardType?: "default" | "decimal-pad";
      multiline?: boolean;
    } = {},
  ) => (
    <View style={{ gap: 6 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          fontWeight: "800",
          color: EMBER.colors.textLight,
        }}
      >
        {label}
      </Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={EMBER.colors.textFaint}
        keyboardType={options.keyboardType ?? "default"}
        multiline={options.multiline}
        style={{
          minHeight: options.multiline ? 76 : 42,
          borderRadius: 13,
          borderWidth: 1,
          borderColor: "rgba(42, 37, 34, 0.09)",
          backgroundColor: "rgba(255, 255, 255, 0.36)",
          paddingHorizontal: 12,
          paddingVertical: options.multiline ? 10 : 0,
          color: EMBER.colors.text,
          fontSize: 13,
          fontFamily: "DMSans_400Regular",
          textAlignVertical: options.multiline ? "top" : "center",
        }}
      />
    </View>
  );

  const renderPaymentForm = () => (
    <View style={{ gap: 14 }}>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 7 }}>
        {(
          [
            ["payment", "Payment"],
            ["claim", "Claim"],
            ["receipt", "Receipt"],
            ["balance", "Balance"],
          ] as const
        ).map(([kind, label]) => {
          const selected = paymentKind === kind;
          return (
            <Pressable
              key={kind}
              onPress={() => {
                capturePress(pressLabel(label));
                setPaymentKind(kind);
              }}
              style={{
                minHeight: 31,
                borderRadius: 999,
                borderWidth: 1,
                borderColor: selected
                  ? "rgba(107, 85, 72, 0.24)"
                  : "rgba(42, 37, 34, 0.08)",
                backgroundColor: selected
                  ? "rgba(107, 85, 72, 0.1)"
                  : "rgba(255, 255, 255, 0.2)",
                paddingHorizontal: 11,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  fontWeight: selected ? "800" : "700",
                  color: selected ? colors.greyBrown : EMBER.colors.textLight,
                }}
              >
                {label}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <View style={{ flexDirection: "row", gap: 9 }}>
        <View style={{ flex: 1 }}>
          {renderPaymentInput("Amount", paymentAmount, setPaymentAmount, "$0", {
            keyboardType: "decimal-pad",
          })}
        </View>
        <View style={{ flex: 1 }}>
          {renderPaymentInput("Date", paymentDate, setPaymentDate, "Today")}
        </View>
      </View>

      {renderPaymentInput(
        "What is this?",
        paymentLabel,
        setPaymentLabel,
        "Medication receipt, clinic deposit, EOB...",
      )}
      {renderPaymentInput(
        "Provider",
        paymentProvider,
        setPaymentProvider,
        "Clinic, pharmacy, lab, insurer...",
      )}
      {renderPaymentInput(
        "Notes",
        paymentNote,
        setPaymentNote,
        "Anything that helps reconcile this later",
        { multiline: true },
      )}

      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(
              paymentAttachmentName ??
                (busySource === "document"
                  ? "Opening..."
                  : "Attach receipt, statement, EOB, or photo"),
            ),
            "attach_payment_document",
          );
          void pickPaymentAttachment();
        }}
        disabled={busySource != null}
        style={{
          minHeight: 44,
          borderRadius: 13,
          borderWidth: 1,
          borderColor: "rgba(42, 37, 34, 0.1)",
          backgroundColor: "rgba(255, 255, 255, 0.24)",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          paddingHorizontal: 12,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            flex: 1,
            minWidth: 0,
          }}
        >
          <FileText size={15} color={colors.greyBrown} strokeWidth={1.8} />
          <Text
            className="font-dm-sans"
            numberOfLines={1}
            style={{
              flex: 1,
              fontSize: 12,
              fontWeight: "800",
              color: colors.greyBrown,
            }}
          >
            {paymentAttachmentName ??
              (busySource === "document"
                ? "Opening..."
                : "Attach receipt, statement, EOB, or photo")}
          </Text>
        </View>
        {paymentAttachmentName ? (
          <Check size={14} color={EMBER.colors.green} strokeWidth={2} />
        ) : (
          <UploadCloud size={14} color={colors.greyBrown} strokeWidth={1.8} />
        )}
      </Pressable>

      <View style={{ flexDirection: "row", gap: 9 }}>
        <Pressable
          onPress={() => {
            capturePress(pressLabel("Cancel"));
            handleClose();
          }}
          style={{
            flex: 1,
            minHeight: 42,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: "rgba(42, 37, 34, 0.1)",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              fontWeight: "800",
              color: EMBER.colors.textLight,
            }}
          >
            Cancel
          </Text>
        </Pressable>
        <Pressable
          onPress={() => {
            capturePress(pressLabel("Add record"));
            void saveManualPayment();
          }}
          disabled={!canSaveManualPayment}
          style={{
            flex: 1,
            minHeight: 42,
            borderRadius: 999,
            backgroundColor: colors.greyBrown,
            alignItems: "center",
            justifyContent: "center",
            opacity: canSaveManualPayment ? 1 : 0.45,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{ fontSize: 12, fontWeight: "800", color: "#fff" }}
          >
            Add record
          </Text>
        </Pressable>
      </View>
    </View>
  );

  const renderChoice = (
    source: UploadedCostDocument["source"],
    label: string,
    Icon: LucideIcon,
    onPress: () => void,
  ) => (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(label));
        onPress();
      }}
      disabled={busySource != null}
      style={{
        flex: 1,
        minWidth: 132,
        minHeight: 108,
        borderRadius: 18,
        borderWidth: 1,
        borderColor: "rgba(42, 37, 34, 0.1)",
        backgroundColor: "rgba(255, 255, 255, 0.44)",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        opacity: busySource && busySource !== source ? 0.55 : 1,
      }}
    >
      <Icon size={28} color={colors.greyBrown} strokeWidth={1.6} />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "700",
          color: EMBER.colors.text,
          textAlign: "center",
        }}
      >
        {busySource === source ? "Opening..." : label}
      </Text>
    </Pressable>
  );

  const copy = documentOnly
    ? {
        title: "Add a document",
        body: "Attach a receipt, statement, EOB, or claim file to this record.",
      }
    : mode === "quote"
      ? {
          title: "Upload a clinic quote",
          body: "Add an itemized estimate, fee schedule, or clinic quote. Louise can read the lines and compare what is included, missing, covered, or financeable.",
        }
      : {
          title: "Add payment or claim",
          body: "Track a payment, claim, receipt, balance, or reimbursement even when the document is not ready yet.",
        };

  return (
    <Modal
      visible
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <Pressable
        onPress={() => {
          // Backdrop dismiss target; it has no visible copy of its own.
          capturePress(pressLabel("dismiss_cost_document_upload"));
          handleClose();
        }}
        style={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          padding: 18,
          backgroundColor: "rgba(42, 37, 34, 0.3)",
        }}
      >
        <Pressable
          onPress={(event) => event.stopPropagation()}
          style={{
            width: "100%",
            maxWidth: 430,
            borderRadius: 24,
            borderWidth: 1,
            borderColor: "rgba(255, 255, 255, 0.52)",
            backgroundColor: "rgba(250, 247, 242, 0.96)",
            padding: 18,
            gap: 16,
            shadowColor: "#000",
            shadowOpacity: 0.14,
            shadowRadius: 24,
            shadowOffset: { width: 0, height: 16 },
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 14,
            }}
          >
            <View style={{ flex: 1, gap: 5 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 18,
                  fontWeight: "800",
                  color: EMBER.colors.text,
                }}
              >
                {copy.title}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 18,
                  color: EMBER.colors.textMid,
                }}
              >
                {copy.body}
              </Text>
            </View>
            <Pressable
              onPress={() => {
                // Icon-only close control with no visible copy.
                capturePress(pressLabel("close_cost_document_upload"));
                handleClose();
              }}
              hitSlop={8}
              style={{
                width: 30,
                height: 30,
                borderRadius: 15,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(42, 37, 34, 0.06)",
              }}
            >
              <X size={15} color={EMBER.colors.textLight} strokeWidth={2} />
            </Pressable>
          </View>

          {mode === "payment" && !documentOnly ? (
            renderPaymentForm()
          ) : (
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
              {renderChoice("camera", "Take a photo", Camera, handleCamera)}
              {renderChoice(
                "document",
                documentOnly ? "Upload document" : "Upload quote file",
                FileText,
                handleDocument,
              )}
            </View>
          )}
          {uploadError ? (
            <View
              accessibilityRole="alert"
              style={{
                borderRadius: 13,
                borderWidth: 1,
                borderColor: "rgba(183, 101, 69, 0.24)",
                backgroundColor: "rgba(183, 101, 69, 0.08)",
                padding: 11,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 17,
                  color: EMBER.colors.coral,
                }}
              >
                {uploadError}
              </Text>
              {pendingUpload || pendingDocument || pendingPaymentAttachment ? (
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(
                        pendingUpload
                          ? "Retry uploading this file"
                          : pendingDocument
                            ? "Retry attaching this file"
                            : "Retry adding record",
                      ),
                    );
                    void (pendingUpload
                      ? retryPendingUpload()
                      : pendingDocument
                        ? retryPendingDocument()
                        : saveManualPayment());
                  }}
                  disabled={busySource != null}
                  style={{
                    alignSelf: "flex-start",
                    marginTop: 9,
                    minHeight: 34,
                    borderRadius: 999,
                    backgroundColor: colors.greyBrown,
                    paddingHorizontal: 12,
                    justifyContent: "center",
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{ fontSize: 11, fontWeight: "800", color: "#fff" }}
                  >
                    {pendingUpload
                      ? "Retry uploading this file"
                      : pendingDocument
                        ? "Retry attaching this file"
                        : "Retry adding record"}
                  </Text>
                </Pressable>
              ) : null}
            </View>
          ) : null}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function AnimatedCurrencyAmount({
  value,
  locale,
  color = EMBER.colors.text,
  testID,
}: {
  value: number;
  locale: Locale;
  color?: string;
  testID?: string;
}) {
  const [displayValue, setDisplayValue] = useState(value);
  const displayValueRef = useRef(value);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const startValue = displayValueRef.current;
    const delta = value - startValue;

    if (delta === 0) return undefined;

    const duration = 340;
    const startTime = Date.now();

    if (frameRef.current != null) {
      cancelAnimationFrame(frameRef.current);
    }

    const step = () => {
      const progress = Math.min((Date.now() - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = Math.round(startValue + delta * eased);

      displayValueRef.current = nextValue;
      setDisplayValue(nextValue);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(step);
      } else {
        displayValueRef.current = value;
        setDisplayValue(value);
        frameRef.current = null;
      }
    };

    frameRef.current = requestAnimationFrame(step);

    return () => {
      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [value]);

  const formattedValue = formatCurrency(displayValue, locale);

  return (
    <Text
      testID={testID}
      className="font-dm-mono"
      style={{
        fontSize: formattedValue.length > 14 ? 24 : 29,
        lineHeight: 32,
        fontWeight: "700",
        color,
        textAlign: "left",
      }}
    >
      {formattedValue}
    </Text>
  );
}

function StateSwitch({
  active,
  onChange,
}: {
  active: ConceptCostState;
  onChange: (state: ConceptCostState) => void;
}) {
  const capturePress = usePressCapture("StateSwitch");
  const options: {
    state: ConceptCostState;
    label: string;
    icon: LucideIcon;
  }[] = [
    { state: "estimate", label: "Estimate", icon: Calculator },
    { state: "clinic-estimate", label: "Quotes", icon: FileCheck2 },
    { state: "payments", label: "Payments", icon: Wallet },
  ];

  return (
    <View
      style={{
        flexDirection: "row",
        gap: 6,
        borderRadius: 999,
        backgroundColor: "rgba(42, 37, 34, 0.06)",
        padding: 4,
      }}
    >
      {options.map((option) => {
        const selected = option.state === active;
        const OptionIcon = option.icon;
        const optionColor = selected
          ? EMBER.colors.text
          : EMBER.colors.textLight;

        return (
          <Pressable
            key={option.state}
            onPress={() => {
              capturePress(pressLabel(option.label));
              onChange(option.state);
            }}
            style={{
              flex: 1,
              minHeight: 28,
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "row",
              gap: 6,
              borderRadius: 999,
              backgroundColor: selected
                ? "rgba(255, 255, 255, 0.78)"
                : "transparent",
              borderWidth: selected ? 1 : 0,
              borderColor: "rgba(42, 37, 34, 0.08)",
            }}
          >
            <OptionIcon size={13} color={optionColor} strokeWidth={1.8} />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: selected ? "600" : "500",
                color: optionColor,
              }}
            >
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function PhysicalStateTabs({
  active,
  onChange,
}: {
  active: ConceptCostState;
  onChange: (state: ConceptCostState) => void;
}) {
  return (
    <CostsCardTabs
      active={active}
      onChange={onChange}
      options={[
        { value: "estimate", label: "Estimate", icon: Calculator },
        { value: "clinic-estimate", label: "Quotes", icon: FileCheck2 },
        { value: "payments", label: "Payments", icon: Wallet },
      ]}
    />
  );
}

function PaymentProgressBar({
  segments,
  leftToPay,
  locale,
  onPay,
  onAdd,
}: {
  segments: PaymentBreakdownSegment[];
  leftToPay: number;
  locale: Locale;
  onPay: () => void;
  onAdd: () => void;
}) {
  const capturePress = usePressCapture("PaymentProgressBar");
  const visibleSegments = segments.filter((segment) => segment.value > 0);
  const total = visibleSegments.reduce(
    (sum, segment) => sum + segment.value,
    0,
  );

  return (
    <View
      style={{
        borderRadius: 18,
        borderWidth: 1,
        borderColor: "rgba(42, 37, 34, 0.08)",
        backgroundColor: "rgba(255, 255, 255, 0.2)",
        padding: 14,
        gap: 12,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <View style={{ flex: 1, minWidth: 0, gap: 2 }}>
          <Text
            className="font-dm-mono"
            style={{
              fontSize: 22,
              fontWeight: "800",
              color: colors.greyBrown,
            }}
          >
            {formatCurrency(leftToPay, locale)}
          </Text>
          <CardSubtitleText style={{ marginTop: 0 }}>
            left to pay for your scenario
          </CardSubtitleText>
        </View>
        <View
          style={{
            flexDirection: "row",
            alignItems: "flex-start",
            gap: 7,
            marginTop: 1,
          }}
        >
          {[
            { label: "Financing", icon: Landmark, onPress: onPay },
            { label: "Add", icon: Plus, onPress: onAdd },
          ].map((action) => {
            const ActionIcon = action.icon;
            return (
              <Pressable
                key={action.label}
                onPress={() => {
                  capturePress(pressLabel(action.label));
                  action.onPress();
                }}
                style={{
                  minHeight: 34,
                  borderRadius: 999,
                  borderWidth: 1,
                  borderColor: "rgba(42, 37, 34, 0.1)",
                  backgroundColor: "rgba(255, 255, 255, 0.28)",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "row",
                  gap: 6,
                  paddingHorizontal: 11,
                }}
              >
                <ActionIcon
                  size={13}
                  color={colors.greyBrown}
                  strokeWidth={action.label === "Add" ? 2 : 1.8}
                />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    fontWeight: "800",
                    color: colors.greyBrown,
                  }}
                >
                  {action.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>

      <View
        style={{
          height: 10,
          borderRadius: 999,
          overflow: "hidden",
          flexDirection: "row",
          backgroundColor: "rgba(42, 37, 34, 0.075)",
        }}
      >
        {visibleSegments.map((segment) => (
          <View
            key={segment.label}
            style={{
              flexGrow: total > 0 ? Math.max(segment.value / total, 0.04) : 1,
              flexBasis: 0,
              backgroundColor: segment.color,
            }}
          />
        ))}
      </View>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
        {segments.map((segment) => (
          <View
            key={segment.label}
            style={{
              flexGrow: 1,
              flexBasis: 88,
              minWidth: 88,
              gap: 4,
            }}
          >
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
            >
              <View
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  backgroundColor: segment.color,
                }}
              />
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  fontSize: 10,
                  fontWeight: "800",
                  color: EMBER.colors.textLight,
                }}
              >
                {segment.label}
              </Text>
            </View>
            <Text
              className="font-dm-mono"
              style={{
                fontSize: 12,
                fontWeight: "800",
                color: EMBER.colors.text,
              }}
            >
              {formatCurrency(segment.value, locale)}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}

function buildLocationOptions({
  profileCity,
  profileState,
  profileZipCode,
  profileCountryCode,
  detectedLocation,
  customLocation,
  locationRequestStatus,
}: {
  profileCity?: string | null;
  profileState?: string | null;
  profileZipCode?: string | null;
  profileCountryCode?: string | null;
  detectedLocation: EstimateAreaOption | null;
  customLocation: EstimateAreaOption | null;
  locationRequestStatus: "idle" | "loading" | "error";
}): ConceptLocationOption[] {
  const profileLocation = buildProfileEstimateAreaOption({
    city: profileCity,
    state: profileState,
    zipCode: profileZipCode,
    countryCode: profileCountryCode,
  });
  const requestLocation: ConceptLocationOption = {
    id: "request-current-location",
    label:
      locationRequestStatus === "loading"
        ? "Locating..."
        : "Use current location",
    detail:
      locationRequestStatus === "error"
        ? "Permission unavailable; search a city"
        : "Use browser or device location",
    source: "fallback",
    action: "request-current-location",
    estimateLocation: { countryCode: "US" },
  };
  const primaryLocation =
    profileLocation ?? detectedLocation ?? buildFallbackEstimateAreaOption();
  const options = [
    primaryLocation,
    ...(detectedLocation && detectedLocation.id !== primaryLocation.id
      ? [detectedLocation]
      : []),
    ...(customLocation ? [customLocation] : []),
    requestLocation,
    ...US_CITY_ESTIMATE_AREA_OPTIONS,
  ];
  const seen = new Set<string>();

  return options.filter((option) => {
    if (seen.has(option.id)) return false;
    seen.add(option.id);
    return true;
  });
}

function getBrowserCoordinates() {
  return new Promise<{ latitude: number; longitude: number }>(
    (resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) =>
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }),
        (error) => reject(error),
        {
          enableHighAccuracy: false,
          maximumAge: 300000,
          timeout: 10000,
        },
      );
    },
  );
}

async function reverseGeocodeWithGoogle({
  latitude,
  longitude,
}: {
  latitude: number;
  longitude: number;
}) {
  const apiKey = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY;
  if (!apiKey) return null;

  const response = await fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`,
  );
  if (!response.ok) return null;
  const data = (await response.json()) as {
    results?: {
      address_components?: {
        long_name: string;
        short_name: string;
        types: string[];
      }[];
    }[];
  };
  const components = data.results?.[0]?.address_components ?? [];
  const findComponent = (type: string, field: "long_name" | "short_name") =>
    components.find((component) => component.types.includes(type))?.[field] ??
    null;

  return {
    city:
      findComponent("locality", "long_name") ??
      findComponent("postal_town", "long_name") ??
      findComponent("administrative_area_level_2", "long_name"),
    state: findComponent("administrative_area_level_1", "short_name"),
    countryCode: findComponent("country", "short_name"),
  };
}

async function requestCurrentEstimateAreaOption() {
  if (Platform.OS === "web") {
    const coordinates = await getBrowserCoordinates();
    const geocode = await reverseGeocodeWithGoogle(coordinates);

    return buildDetectedEstimateAreaOption({
      ...coordinates,
      city: geocode?.city,
      state: geocode?.state,
      countryCode: geocode?.countryCode,
    });
  }

  const { status } = await ExpoLocation.requestForegroundPermissionsAsync();
  if (status !== "granted") {
    throw new Error("Location permission denied");
  }
  const position = await ExpoLocation.getCurrentPositionAsync({
    accuracy: ExpoLocation.Accuracy.Balanced,
  });
  const geocode = await ExpoLocation.reverseGeocodeAsync({
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
  });
  const firstResult = geocode[0];

  return buildDetectedEstimateAreaOption({
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    city: firstResult?.city ?? firstResult?.subregion,
    state: firstResult?.region,
    countryCode: firstResult?.isoCountryCode ?? "US",
  });
}

function InsightBox({
  icon: Icon,
  title,
  body,
}: {
  icon: LucideIcon;
  title: string;
  body: string;
}) {
  return (
    <View
      style={{
        borderRadius: 14,
        borderWidth: 1,
        borderColor: "rgba(42, 37, 34, 0.08)",
        backgroundColor: "rgba(255, 255, 255, 0.24)",
        padding: 14,
        gap: 7,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 7 }}>
        <Icon size={14} color={EMBER.colors.textLight} strokeWidth={1.6} />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "700",
            color: EMBER.colors.text,
          }}
        >
          {title}
        </Text>
      </View>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 18,
          color: EMBER.colors.textMid,
        }}
      >
        {body}
      </Text>
    </View>
  );
}

function EstimateCheckbox({ selected }: { selected: boolean }) {
  const selectedColor = colors.greyBrown;

  return (
    <View
      style={{
        width: 19,
        height: 19,
        borderRadius: 5,
        borderWidth: 1,
        borderColor: selected ? selectedColor : "rgba(42, 37, 34, 0.24)",
        backgroundColor: selected ? selectedColor : "rgba(255, 255, 255, 0.42)",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {selected ? <Check size={13} color="#fff" strokeWidth={2.4} /> : null}
    </View>
  );
}

function EstimateStateIcon({ selected }: { selected: boolean }) {
  return (
    <View
      style={{
        width: 20,
        height: 20,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {selected ? (
        <CheckCircle2 size={16} color={EMBER.colors.text} strokeWidth={1.9} />
      ) : (
        <Plus size={15} color={EMBER.colors.textLight} strokeWidth={2} />
      )}
    </View>
  );
}

function getLineAmount(
  line: EstimateScenarioLine,
  params: {
    cycleCount: number;
    embryoCount: number;
    storageYears: number;
    transferCount: number;
  },
) {
  if (line.multiplier === "cycle") return line.amount * params.cycleCount;
  if (line.multiplier === "embryo") return line.amount * params.embryoCount;
  if (line.multiplier === "storage-year") {
    return line.amount * params.storageYears;
  }
  if (line.multiplier === "transfer") return line.amount * params.transferCount;
  return line.amount;
}

function getStatusLabel(status: EstimateLineStatus) {
  if (status === "likely-included") return "Confirm bundle";
  if (status === "often-separate") return "Often billed separately";
  return "Optional add-on";
}

function getStatusColor(status: EstimateLineStatus) {
  if (status === "likely-included") return EMBER.colors.textFaint;
  if (status === "often-separate") return colors.greyBrown;
  return EMBER.colors.textMid;
}

function uniqueItems(items: string[]) {
  return Array.from(new Set(items.filter(Boolean)));
}

function formatScenarioList(items: string[]) {
  const unique = uniqueItems(items);
  if (unique.length === 0) return "";
  if (unique.length === 1) return unique[0]!;
  if (unique.length === 2) return `${unique[0]} and ${unique[1]}`;
  return `${unique.slice(0, -1).join(", ")}, and ${unique[unique.length - 1]}`;
}

function limitScenarioItems(items: string[], maxItems = 4) {
  const unique = uniqueItems(items);
  if (unique.length <= maxItems) return unique;
  return [...unique.slice(0, maxItems), "other add-ons"];
}

function sentenceCase(value: string) {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatScenarioCount(
  count: number,
  singular: string,
  plural = `${singular}s`,
) {
  return `${count === 1 ? "one" : count} ${count === 1 ? singular : plural}`;
}

function getScenarioAreaPhrase(
  location: ConceptLocationOption,
  locationIsSpecific: boolean,
) {
  // Only claim "in this location" when the catalog actually prices this place
  // below the country level. Otherwise it's a national average regardless of
  // the picked label (the catalog is country-only today), so say so.
  if (!locationIsSpecific) return "using U.S. averages";
  const label = location.label.trim();
  if (!label || label === "Your area") return "using U.S. averages";
  // Custom locations priced through a nearest supported area shouldn't claim
  // to be "in" the place the person asked for.
  if (location.estimateAreaLabel) return `near ${location.estimateAreaLabel}`;
  return "in this location";
}

// Human-readable label for the location the estimate actually uses, for
// analytics comparison against the location the person asked for.
function getEstimateAreaAnalyticsLabel(option: ConceptLocationOption) {
  if (option.estimateAreaLabel) return option.estimateAreaLabel;
  const { city, stateCode, countryCode } = option.estimateLocation;
  if (city && stateCode) return `${city}, ${stateCode}`;
  if (stateCode) return getUsStateName(stateCode) ?? stateCode;
  return countryCode;
}

// Notice shown under the estimate for a searched location, naming both the
// requested place and the estimate location actually used. Derived from the
// selected option so it re-appears when the person re-selects a saved custom
// location. Returns null for supported/known selections that need no notice.
function deriveLocationNotice(option: ConceptLocationOption): string | null {
  if (option.source !== "custom" || !option.estimateAreaLabel) return null;
  if (option.estimateLocation.stateCode) {
    return `We don't have detailed estimates for ${option.label}. Showing the estimate for the closest available location: ${option.estimateAreaLabel}.`;
  }
  return `We don't have city-level estimates for ${option.label}. Showing the ${option.estimateAreaLabel} national estimate.`;
}

function getTreatmentSentenceLabel(label: string) {
  if (
    label.includes("IVF") ||
    label.includes("ICSI") ||
    label.includes("IUI")
  ) {
    return label;
  }
  return label.charAt(0).toLowerCase() + label.slice(1);
}

function getMedicationIntensityPhrase(
  medicationIntensity: "low" | "typical" | "high",
  treatment: TreatmentType,
) {
  const medicationLabel =
    treatment === "fet"
      ? "transfer meds"
      : treatment === "iui"
        ? "IUI meds"
        : treatment === "ovulation_induction"
          ? "ovulation meds"
          : "stimulation meds";
  if (medicationIntensity === "typical") return `typical ${medicationLabel}`;
  if (medicationIntensity === "low") {
    return `lower-intensity ${medicationLabel}`;
  }
  return `higher-dose ${medicationLabel}`;
}

function getScenarioLineShortLabel(line: EstimateScenarioLine) {
  switch (line.key) {
    case "consult":
      return "consult";
    case "female-workup":
    case "male-workup":
      return "pre-cycle labs";
    case "uterine-cavity":
      return "cavity check";
    case "anesthesia":
      return "retrieval anesthesia";
    case "medications":
      return "medications";
    case "icsi":
      return "ICSI";
    case "pgta":
      return "PGT-A";
    case "pgtm-sr":
      return "PGT-M/SR";
    case "pgtp":
      return "PGT-P";
    case "assisted-hatching":
      return "assisted hatching";
    case "sperm-wash":
      return "sperm prep";
    case "donor-sperm":
      return "donor sperm";
    case "donor-eggs":
      return "donor eggs";
    case "donor-embryo":
      return "donor embryo";
    case "known-donor-coordination":
      return "known-donor steps";
    case "storage":
      return "storage";
    case "specimen-transfer":
      return "shipping";
    case "additional-fet":
      return "another transfer";
    case "transfer-meds":
      return "transfer medications";
    case "extra-cycle-buffer":
      return "another cycle";
    default:
      return line.label.toLowerCase();
  }
}

function getSeparateScenarioGroup(
  section: EstimateScenarioSection,
  line: EstimateScenarioLine,
) {
  if (section.key === "before-cycle") return "testing";
  if (section.key === "cycle" && line.key === "anesthesia") {
    return "anesthesia";
  }
  if (section.key === "medications") return "medications";
  if (section.key === "lab-add-ons") return getScenarioLineShortLabel(line);
  if (section.key === "storage") return "storage";
  if (section.key === "transfer-path") return "transfer costs";
  return section.title.toLowerCase();
}

function buildScenarioNarrative({
  treatment,
  treatmentLabel,
  location,
  locationIsSpecific,
  sections,
  selectedLineKeys,
  cycleCount,
  medicationIntensity,
  embryoCount,
  storageYears,
  transferCount,
}: {
  treatment: TreatmentType;
  treatmentLabel: string;
  location: ConceptLocationOption;
  locationIsSpecific: boolean;
  sections: EstimateScenarioSection[];
  selectedLineKeys: string[];
  cycleCount: number;
  medicationIntensity: "low" | "typical" | "high";
  embryoCount: number;
  storageYears: number;
  transferCount: number;
}) {
  const selectedKeySet = new Set(selectedLineKeys);
  const selectedItems = sections.flatMap((section) =>
    section.lines
      .filter((line) => selectedKeySet.has(line.key))
      .map((line) => ({ section, line })),
  );
  const selectedKeys = new Set(selectedItems.map(({ line }) => line.key));
  const hasSelectedLine = (key: string) => selectedKeys.has(key);

  const scenarioParts: string[] = [];
  const hasCyclePackage = selectedItems.some(({ line }) =>
    [
      "egg-freezing-cycle",
      "embryo-freezing-cycle",
      "ivf-cycle",
      "iui-cycle",
      "oi-cycle",
      "sperm-freezing-cycle",
    ].includes(line.key),
  );
  const hasTransferPackage =
    hasSelectedLine("fet-cycle") || hasSelectedLine("additional-fet");
  const medicationPhrase = hasSelectedLine("medications")
    ? getMedicationIntensityPhrase(medicationIntensity, treatment)
    : null;

  if (hasCyclePackage) {
    const cyclePhrase = formatScenarioCount(cycleCount, "cycle");
    scenarioParts.push(cyclePhrase);
    if (medicationPhrase) {
      scenarioParts.push(medicationPhrase);
    }
  } else if (medicationPhrase) {
    scenarioParts.push(medicationPhrase);
  }
  if (hasTransferPackage) {
    scenarioParts.push(formatScenarioCount(transferCount, "transfer"));
  }
  if (hasSelectedLine("pgta") || hasSelectedLine("pgtp")) {
    scenarioParts.push(`${formatScenarioCount(embryoCount, "embryo")} for PGT`);
  }
  if (hasSelectedLine("storage")) {
    scenarioParts.push(
      `${formatScenarioCount(storageYears, "year")} of storage`,
    );
  }
  if (
    hasSelectedLine("donor-sperm") ||
    hasSelectedLine("donor-eggs") ||
    hasSelectedLine("donor-embryo") ||
    hasSelectedLine("known-donor-coordination")
  ) {
    scenarioParts.push("donor-related costs");
  }

  const separateGroups = selectedItems
    .filter(
      ({ line }) =>
        line.status === "often-separate" &&
        line.key !== "medications" &&
        line.key !== "storage",
    )
    .map(({ section, line }) => getSeparateScenarioGroup(section, line));
  const selectedOptionalItems = selectedItems
    .filter(({ line }) => line.status === "optional")
    .map(({ line }) => getScenarioLineShortLabel(line));
  const unselectedOptionalItems = sections.flatMap((section) =>
    section.lines
      .filter(
        (line) => line.status === "optional" && !selectedKeySet.has(line.key),
      )
      .map((line) => getScenarioLineShortLabel(line)),
  );

  const firstSentence = `For ${getTreatmentSentenceLabel(
    treatmentLabel,
  )} ${getScenarioAreaPhrase(location, locationIsSpecific)}, this assumes ${
    scenarioParts.length > 0
      ? formatScenarioList(scenarioParts)
      : "the selected items"
  }.`;

  const includedItems = limitScenarioItems(
    [...separateGroups, ...selectedOptionalItems],
    3,
  );
  const excludedOptional = limitScenarioItems(unselectedOptionalItems, 2);
  const includedList = formatScenarioList(includedItems);
  const excludedList = formatScenarioList(excludedOptional);

  if (includedItems.length > 0 && excludedOptional.length > 0) {
    return `${firstSentence} ${sentenceCase(includedList)} ${
      includedItems.length === 1 ? "is" : "are"
    } included. ${sentenceCase(excludedList)} ${
      excludedOptional.length === 1 ? "is" : "are"
    } not.`;
  }

  if (includedItems.length > 0) {
    return `${firstSentence} ${sentenceCase(includedList)} ${
      includedItems.length === 1 ? "is" : "are"
    } included too.`;
  }

  if (excludedOptional.length > 0) {
    return `${firstSentence} ${sentenceCase(excludedList)} ${
      excludedOptional.length === 1 ? "is" : "are"
    } not included.`;
  }

  return `${firstSentence} Add or remove items in Scenario Builder as the plan changes.`;
}

function getCostStateSummary(params: {
  state: ConceptCostState;
  treatmentLabel: string;
  estimateTotal: string;
  patientEstimate: string | null;
  quoteTotal: string;
  quoteCount: number;
  comparableQuoteCount: number;
  paymentDue: string;
  hasInsurance: boolean;
  coverageVerified: boolean;
  insuranceLabel: string;
  clinicCount: number;
  verifiedClinicCount: number;
}): CostStateSummary {
  if (params.state === "clinic-estimate") {
    return {
      eyebrow: "Clinic estimate workspace",
      title: "Compare quotes before money moves",
      amount: params.quoteTotal,
      amountLabel:
        params.comparableQuoteCount > 1
          ? "lowest uploaded quote before insurance"
          : params.comparableQuoteCount === 1
            ? "uploaded quote before insurance"
            : params.quoteCount > 0
              ? "uploaded quote needs review"
              : "no clinic quotes uploaded",
      description:
        "Once a clinic shares an official estimate, this view compares included lines, likely surprise charges, insurance fit, and financing paths.",
      badge: params.quoteCount > 0 ? "Clinic quote" : "Awaiting quote",
      badgeTone: "quote",
      rows: [
        {
          label: "Quotes on file",
          value:
            params.quoteCount > 0
              ? `${params.quoteCount} uploaded`
              : "None uploaded",
          detail:
            params.quoteCount > 0
              ? "Compare itemized lines, exclusions, and patient responsibility."
              : "Upload a clinic quote or request one from a selected clinic.",
        },
        {
          label: "Insurance read",
          value: params.insuranceLabel,
          detail: params.coverageVerified
            ? "Verified plan details can be applied to clinic line items."
            : "A benefits check is still needed before this is plan-aware.",
        },
        {
          label: "Decision point",
          value: params.quoteCount > 0 ? "Compare out-of-pocket" : "Get quote",
          detail:
            "Ask clinics to itemize cycle fees, meds, storage, labs, anesthesia, and add-ons.",
        },
      ],
      primaryAction:
        params.quoteCount > 0 ? "Compare quotes" : "Review insurance",
      secondaryAction: "Financing options",
      primaryRoute: params.quoteCount > 0 ? undefined : "Insurance",
      secondaryRoute: "Financing",
    };
  }

  if (params.state === "payments") {
    return {
      eyebrow: "Payments and claims",
      title: "Track what is billed, covered, and due",
      amount: params.paymentDue,
      amountLabel: "left to pay after recorded payments",
      description:
        "After treatment starts, this view tracks pre-cycle payments, deposits, claims, receipts, reimbursements, and what needs attention next.",
      badge: "Active care",
      badgeTone: "payments",
      rows: [
        {
          label: "Clinic balance",
          value: "Needs statement",
          detail:
            "Connect or upload clinic statements to separate charges from payments.",
        },
        {
          label: "Insurance claims",
          value: params.coverageVerified ? "Ready to match" : "Pending plan",
          detail: params.coverageVerified
            ? "EOBs can be matched against quote and payment lines."
            : "Verified coverage unlocks claim-level reconciliation.",
        },
        {
          label: "Receipts",
          value: "Tax-ready folder",
          detail: "Keep medication, storage, lab, and travel receipts grouped.",
        },
      ],
      primaryAction: "Review insurance",
      secondaryAction: "Tax tracker",
      primaryRoute: "Insurance",
      secondaryRoute: "TaxDeductions",
    };
  }

  return {
    eyebrow: "Planning estimate",
    title: `Start with a ${params.treatmentLabel} ballpark`,
    amount: params.patientEstimate ?? params.estimateTotal,
    amountLabel: params.patientEstimate
      ? "estimated out-of-pocket"
      : "estimated before insurance",
    description:
      "Use this estimate while exploring. It updates as coverage is verified, clinics are compared, and itemized quotes arrive.",
    badge: params.patientEstimate ? "Plan-aware estimate" : "Estimate",
    badgeTone: "estimate",
    rows: [
      {
        label: "Treatment path",
        value: params.treatmentLabel,
        detail:
          "Includes common diagnostics, medications, and one year storage.",
      },
      {
        label: "Insurance status",
        value: params.insuranceLabel,
        detail: params.hasInsurance
          ? "Coverage verification controls whether this can show likely out-of-pocket."
          : "Add insurance to move from total charges toward responsibility.",
      },
      {
        label: "Next best step",
        value: params.hasInsurance ? "Compare clinics" : "Check coverage",
        detail: "Clinic quotes can replace public estimates.",
      },
    ],
    primaryAction: params.hasInsurance ? "Review insurance" : "Check coverage",
    secondaryAction: "Financing options",
    primaryRoute: params.hasInsurance ? "Insurance" : undefined,
    secondaryRoute: "Financing",
  };
}

function AdaptiveCostStatusCard({
  state,
  onStateChange,
  initialTreatmentType,
  diagnosticProfile,
  locale,
  profileCity,
  profileState,
  profileZipCode,
  profileCountryCode,
  hasInsurance,
  coverageVerified,
  verificationStatus,
  insuranceLabel,
  fertilityCoverage,
  primaryPolicy,
  clinicCount,
  verifiedClinicCount,
  financialPlanLabel,
  onAddInsurance,
  onNavigate,
  onViewAllPayments,
  onOpenChat,
  onCompareQuotes,
  initialUploadMode,
  onUploadModeConsumed,
  actionButtonStyle = "default",
  topControlsVariant = "pills",
  hideInternalStateSwitch = false,
}: {
  state: ConceptCostState;
  onStateChange: (state: ConceptCostState) => void;
  initialTreatmentType: TreatmentType;
  diagnosticProfile: DiagnosticProfile;
  locale: Locale;
  profileCity?: string | null;
  profileState?: string | null;
  profileZipCode?: string | null;
  profileCountryCode?: string | null;
  hasInsurance: boolean;
  coverageVerified: boolean;
  verificationStatus: "unverified" | "pending" | "verified";
  insuranceLabel: string;
  fertilityCoverage: FertilityCoverage | null;
  primaryPolicy: InsurancePolicy | null;
  clinicCount: number;
  verifiedClinicCount: number;
  financialPlanLabel: string | null;
  onAddInsurance: () => void;
  onNavigate: (
    route: CostsOverviewRouteName,
    params?: CostsOverviewRouteParams,
  ) => void;
  onViewAllPayments: (params: CostPaymentsRouteParams) => void;
  onOpenChat: (record: CostPaymentRecordParam) => void;
  onCompareQuotes: (context: QuoteComparisonContext) => void;
  initialUploadMode?: CostUploadMode;
  onUploadModeConsumed?: () => void;
  actionButtonStyle?: "default" | "care";
  topControlsVariant?: "pills" | "bar";
  hideInternalStateSwitch?: boolean;
}) {
  const capturePress = usePressCapture("AdaptiveCostStatusCard");
  const [selectedTreatment, setSelectedTreatment] =
    useState<TreatmentType>(initialTreatmentType);
  const [selectedLocationId, setSelectedLocationId] = useState("profile");
  useEffect(() => {
    setSelectedTreatment(initialTreatmentType);
  }, [initialTreatmentType]);

  // The last searched location that resolved to a nearest supported area or a
  // national estimate. Stays in the option list after selecting something else
  // so the entered location is still there when the person comes back to edit.
  const [customLocation, setCustomLocation] =
    useState<ConceptLocationOption | null>(null);
  const [locationSearchHint, setLocationSearchHint] = useState<string | null>(
    null,
  );
  // Query for the searchable location control; predictions come from the
  // shared usePlacesPredictions hook below.
  const [locationQuery, setLocationQuery] = useState("");
  const [selectedLineKeys, setSelectedLineKeys] = useState<string[]>([]);
  const [cycleCount, setCycleCount] = useState(1);
  const [medicationIntensity, setMedicationIntensity] = useState<
    "low" | "typical" | "high"
  >("typical");
  const [embryoCount, setEmbryoCount] = useState(4);
  const [storageYears, setStorageYears] = useState(1);
  const [transferCount, setTransferCount] = useState(1);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [detectedLocation, setDetectedLocation] =
    useState<EstimateAreaOption | null>(null);
  const [openAssumptionControl, setOpenAssumptionControl] = useState<
    "treatment" | "location" | null
  >(null);
  const [locationRequestStatus, setLocationRequestStatus] = useState<
    "idle" | "loading" | "error"
  >("idle");
  const [uploadModalMode, setUploadModalMode] = useState<CostUploadMode | null>(
    null,
  );
  // In-flight OCR extraction state for uploaded quotes, keyed by clinic-quote id.
  // Completed extractions live on the session-only clinic-quote record.
  const transfer = useDocumentSourceTransfer();
  const { capture: captureEstimateLocationIntent } =
    useEstimateLocationIntent();
  const estimateFlow = useFlowAnalytics("coverage_estimate");
  // An estimate is "completed" once it is priced for somewhere the person
  // chose, not the moment a number appears — everyone lands on a number from
  // the profile default, so counting that would flatten the funnel to 100%.
  const estimateLocationChosen = useRef(false);
  // Choosing a location is both the domain fact the estimate team reads
  // (`cost_estimate_location_requested`) and the step the funnel turns on, so
  // the two are recorded from one place rather than drifting apart.
  const captureLocationIntent = useCallback(
    (
      event: Parameters<
        ReturnType<typeof useEstimateLocationIntent>["capture"]
      >[0],
    ) => {
      captureEstimateLocationIntent(event);
      estimateFlow.stepSubmitted("location", {
        selection_source: event.selectionSource,
        match_type: event.matchType,
      });
      if (event.matchType === "unsupported") return;
      if (estimateLocationChosen.current) return;
      estimateLocationChosen.current = true;
      estimateFlow.completed({
        selection_source: event.selectionSource,
        match_type: event.matchType,
      });
    },
    [captureEstimateLocationIntent, estimateFlow],
  );
  const [quoteExtractions, setQuoteExtractions] = useState<
    Record<
      string,
      {
        status: "extracting" | "done" | "error";
        data?: QuoteOCRResult;
        error?: string;
      }
    >
  >({});
  const paymentRecordsQuery = usePaymentRecords();
  const clinicQuotesQuery = useClinicQuotes();
  const createPaymentRecordMutation = useCreatePaymentRecord();
  const deletePaymentRecordMutation = useDeletePaymentRecord();
  const updatePaymentRecordMutation = useUpdatePaymentRecord();
  const createClinicQuoteMutation = useCreateClinicQuote();
  const deleteClinicQuoteMutation = useDeleteClinicQuote();
  const updateClinicQuoteMutation = useUpdateClinicQuote();
  const attemptedQuoteExtractionsRef = useRef<Set<string>>(new Set());
  const [expandedQuoteIds, setExpandedQuoteIds] = useState<string[]>([]);
  const [quoteDeleteTargetId, setQuoteDeleteTargetId] = useState<string | null>(
    null,
  );
  const [quoteDeleteError, setQuoteDeleteError] = useState<string | null>(null);
  const [selectedPaymentRecordId, setSelectedPaymentRecordId] = useState<
    string | null
  >(null);
  const [attachmentRecordId, setAttachmentRecordId] = useState<string | null>(
    null,
  );
  const [deletedPaymentRecordIds, setDeletedPaymentRecordIds] = useState<
    string[]
  >([]);
  const [paymentDocumentAttachments, setPaymentDocumentAttachments] = useState<
    Record<string, PaymentDocumentLine[]>
  >({});
  const [editedPaymentRecords, setEditedPaymentRecords] = useState<
    Record<string, PaymentRecord>
  >({});
  const previousStateRef = useRef(state);

  const locationOptions = useMemo(
    () =>
      buildLocationOptions({
        profileCity,
        profileState,
        profileZipCode,
        profileCountryCode,
        detectedLocation,
        customLocation,
        locationRequestStatus,
      }),
    [
      customLocation,
      detectedLocation,
      locationRequestStatus,
      profileCity,
      profileCountryCode,
      profileState,
      profileZipCode,
    ],
  );
  const selectedLocation =
    locationOptions.find((option) => option.id === selectedLocationId) ??
    locationOptions[0]!;

  useEffect(() => {
    if (!locationOptions.some((option) => option.id === selectedLocationId)) {
      setSelectedLocationId(locationOptions[0]?.id ?? "fallback");
    }
  }, [locationOptions, selectedLocationId]);

  useEffect(() => {
    if (!initialUploadMode) return;
    setUploadModalMode(initialUploadMode);
    onUploadModeConsumed?.();
  }, [initialUploadMode, onUploadModeConsumed]);

  useEffect(() => {
    const fixture = getDevCostsLocationFixture();
    if (!fixture) return;
    const resolution = resolvePlaceEstimateArea(fixture);
    if (resolution.status === "exact") {
      setSelectedLocationId(resolution.option.id);
    } else if (
      resolution.status === "nearest" ||
      resolution.status === "country"
    ) {
      setCustomLocation(resolution.option);
      setSelectedLocationId(resolution.option.id);
    } else {
      setLocationSearchHint(resolution.hint);
    }
  }, []);

  useEffect(() => {
    if (previousStateRef.current === state) return;

    setOpenAssumptionControl(null);
    setShowBreakdown(false);
    setShowInfo(false);
    setUploadModalMode(null);
    setExpandedQuoteIds([]);
    setSelectedPaymentRecordId(null);
    previousStateRef.current = state;
  }, [state]);

  const estimateSections = useMemo(
    () =>
      buildEstimateScenarioSections({
        treatment: selectedTreatment,
        diagnosticProfile,
        medicationIntensity,
        estimateLocation: selectedLocation.estimateLocation,
      }),
    [
      selectedTreatment,
      diagnosticProfile,
      medicationIntensity,
      selectedLocation.estimateLocation,
    ],
  );

  const defaultLineKeys = useMemo(
    () =>
      estimateSections.flatMap((section) =>
        section.lines
          .filter((line) => line.defaultSelected)
          .map((line) => line.key),
      ),
    [estimateSections],
  );
  const defaultLineKeySignature = defaultLineKeys.join("|");
  const activeLineKeys =
    selectedLineKeys.length > 0 ? selectedLineKeys : defaultLineKeys;
  const selectedLineKeySet = useMemo(
    () => new Set(activeLineKeys),
    [activeLineKeys],
  );

  useEffect(() => {
    setSelectedLineKeys(defaultLineKeys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTreatment, diagnosticProfile, defaultLineKeySignature]);

  useEffect(() => {
    if (!hasTransferPath(selectedTreatment)) {
      setTransferCount(1);
    }
  }, [selectedTreatment]);

  const adjustedBreakdown = estimateSections.flatMap((section) =>
    section.lines
      .filter((line) => selectedLineKeySet.has(line.key))
      .map((line) => ({
        key: line.key,
        label: line.label,
        amount: getLineAmount(line, {
          cycleCount,
          embryoCount,
          storageYears,
          transferCount,
        }),
        detail: getStatusLabel(line.status),
      })),
  );
  const adjustedTotal = adjustedBreakdown.reduce(
    (sum, item) => sum + item.amount,
    0,
  );
  const currentPlanEstimate = coverageVerified
    ? estimatePatientResponsibility(adjustedTotal, fertilityCoverage)
    : null;
  const modeledPlanEstimate = Math.round(adjustedTotal * 0.42);
  const patientEstimate = hasInsurance
    ? (currentPlanEstimate ?? modeledPlanEstimate)
    : null;
  const expectedPatientResponsibility =
    patientEstimate ?? Math.round(adjustedTotal * 0.34);
  const treatmentLabel = TREATMENT_LABELS[selectedTreatment];
  const activeInsuranceLabel = hasInsurance ? insuranceLabel : "Self-pay";
  const scenarioNarrative = buildScenarioNarrative({
    treatment: selectedTreatment,
    treatmentLabel,
    location: selectedLocation,
    locationIsSpecific: isLocationSpecificEstimate(
      selectedLocation.estimateLocation,
    ),
    sections: estimateSections,
    selectedLineKeys: activeLineKeys,
    cycleCount,
    medicationIntensity,
    embryoCount,
    storageYears,
    transferCount,
  });
  const cardNarrative = state === "estimate" ? scenarioNarrative : null;

  const isEstimateView = state === "estimate";
  // Read, never depended on: switching treatment mid-estimate is a step
  // inside the run, not the start of a new one.
  const treatmentRef = useRef(selectedTreatment);
  treatmentRef.current = selectedTreatment;
  useEffect(() => {
    if (!isEstimateView) return;
    estimateFlow.started({ treatment: treatmentRef.current });
    return () => {
      if (estimateLocationChosen.current) return;
      estimateFlow.abandoned("left_without_choosing_location", {
        treatment: treatmentRef.current,
      });
    };
  }, [estimateFlow, isEstimateView]);
  // Never silently swap in another location's estimate: name both the place the
  // person asked for and the estimate we could actually use, right under the
  // number. The message depends on how the selection resolved:
  //   - nearest metro (custom option with a sub-country estimate location)
  //   - national estimate (custom option priced at the country level)
  //   - unsupported country (kept the prior selection, holds a transient hint)
  const locationNoticeMessage =
    locationSearchHint ?? deriveLocationNotice(selectedLocation);

  const parseQuoteAmount = (value: string | null | undefined): number => {
    const n = Number(String(value ?? "").replace(/[^0-9.]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };
  const devClinicQuoteFixtures = getDevClinicQuoteFixtures();
  const savedClinicQuotes = [
    ...devClinicQuoteFixtures,
    ...(clinicQuotesQuery.data ?? []),
  ];
  const uploadedQuoteCards: ClinicQuote[] = savedClinicQuotes.map(
    (quote, index) => {
      const extraction = quoteExtractions[quote.id];
      const data = quote.extraction ?? extraction?.data;
      const status = quote.extraction
        ? "done"
        : (extraction?.status ?? (quote.uploadId ? "extracting" : "error"));
      const document = { id: quote.id, name: quote.name };
      const fallbackName =
        index === 0 ? "Uploaded clinic quote" : `Uploaded quote ${index + 1}`;
      const clinicName = data?.clinicName?.trim() || fallbackName;
      const uploadedAt = "Uploaded quote";

      const extractedLines = (data?.lineItems ?? []).filter(
        (line) =>
          (line.label && line.label.trim().length > 0) ||
          parseQuoteAmount(line.amount) > 0,
      );
      const extractedTotal = data?.totalAmount
        ? parseQuoteAmount(data.totalAmount)
        : extractedLines.reduce(
            (sum, line) => sum + parseQuoteAmount(line.amount),
            0,
          );

      if (status === "extracting") {
        return {
          id: document.id,
          clinic: clinicName,
          location: selectedLocation.label,
          logoText: "UQ",
          status: "Analyzing…",
          statusTone: "pending",
          quoteTotal: 0,
          outOfPocket: 0,
          uploadedAt,
          documentLabel: document.name,
          quoteDate: data?.quoteDate ?? null,
          summary: "Reading the document to pull out line items and totals…",
          analysisStatus: "ready",
          analysisTitle: "Analyzing the quote",
          analysisBody: "Louise is reading the uploaded document.",
          missingItems: [],
          lineItems: [],
        };
      }

      if (status === "error" || extractedLines.length === 0) {
        return {
          id: document.id,
          clinic: clinicName,
          location: selectedLocation.label,
          logoText: "UQ",
          status: "Couldn't read",
          statusTone: "attention",
          quoteTotal: extractedTotal,
          outOfPocket: 0,
          uploadedAt,
          documentLabel: document.name,
          quoteDate: data?.quoteDate ?? null,
          summary:
            extraction?.error ??
            "No itemized charges could be read from this document. It's still saved to your documents.",
          analysisStatus: "needs-upload",
          analysisTitle: "No line items found",
          analysisBody:
            "The document is attached, but Louise couldn't read itemized charges from it.",
          missingItems: [],
          lineItems: [],
        };
      }

      // Extraction succeeded with real line items.
      return {
        id: document.id,
        clinic: clinicName,
        location: selectedLocation.label,
        logoText: "UQ",
        status: "Analyzed",
        statusTone: "ready",
        quoteTotal: extractedTotal,
        outOfPocket: expectedPatientResponsibility,
        uploadedAt,
        documentLabel: document.name,
        quoteDate: data?.quoteDate ?? null,
        summary: `Read ${extractedLines.length} line ${
          extractedLines.length === 1 ? "item" : "items"
        } from the uploaded quote${
          data?.clinicName?.trim() ? ` from ${data.clinicName.trim()}` : ""
        }.`,
        analysisStatus: "ready",
        analysisTitle: "Quote analyzed",
        analysisBody:
          "These line items were read from your uploaded document. Out-of-pocket remains a projection based on your coverage.",
        missingItems: [],
        lineItems: extractedLines.map((line, lineIndex) => ({
          key: `${document.id}-line-${lineIndex}`,
          label: line.label?.trim() || `Line ${lineIndex + 1}`,
          amount: parseQuoteAmount(line.amount),
          category: line.category?.trim() || null,
          status: "From quote",
          coverage: "Check coverage",
          financing: "—",
          detail: line.notes?.trim()
            ? line.notes.trim()
            : line.category?.trim()
              ? `Category: ${line.category.trim()}`
              : "Read from the uploaded quote.",
        })),
      };
    },
  );
  const quoteCards = uploadedQuoteCards;
  const comparableQuoteCards = quoteCards.filter(
    (quote) =>
      quote.statusTone === "ready" &&
      Number.isFinite(quote.quoteTotal) &&
      quote.quoteTotal >= 0,
  );
  const uploadedQuoteCount = comparableQuoteCards.length;
  const quoteSummaryAmount =
    uploadedQuoteCount > 0
      ? Math.min(...comparableQuoteCards.map((quote) => quote.quoteTotal))
      : null;

  const savedPaymentRecords = paymentRecordsQuery.data ?? [];
  const devPaymentRecordFixtures = getDevPaymentRecordFixtures();
  const summaryRecords =
    devPaymentRecordFixtures ??
    savedPaymentRecords.map((record) => apiToUiPaymentRecord(record, locale));
  const {
    paid: paidToDate,
    claims: submittedClaimAmount,
    leftToPay,
  } = summarizePaymentRecords(summaryRecords, expectedPatientResponsibility);
  // Only domain data is stored; the mapper derives all display copy at render
  // time (locale-aware headlines, date labels, split-line colors, ...).
  const basePaymentRecords: PaymentRecord[] = savedPaymentRecords.map(
    (record) => apiToUiPaymentRecord(record, locale),
  );
  const mappedPaymentRecords = basePaymentRecords.map((record) => {
    const attachments = paymentDocumentAttachments[record.id] ?? [];
    const withAttachments =
      attachments.length > 0
        ? { ...record, documents: [...attachments, ...record.documents] }
        : record;
    const editedRecord = editedPaymentRecords[record.id];

    return editedRecord
      ? {
          ...withAttachments,
          ...editedRecord,
          documents: withAttachments.documents,
        }
      : withAttachments;
  });
  const paymentRecords = (
    devPaymentRecordFixtures ?? mappedPaymentRecords
  ).filter((record) => !deletedPaymentRecordIds.includes(record.id));
  const selectedPaymentRecord =
    paymentRecords.find((record) => record.id === selectedPaymentRecordId) ??
    null;
  const paymentBreakdownSegments: PaymentBreakdownSegment[] = [
    {
      label: "Paid",
      value: paidToDate,
      detail: "Recorded",
      color: ORGANIC_BAR_COLORS.paid,
    },
    {
      label: "Claims",
      value: submittedClaimAmount,
      detail: coverageVerified ? "In review" : "Needs plan",
      color: ORGANIC_BAR_COLORS.claims,
    },
    {
      label: "Left",
      value: leftToPay,
      detail: "Expected",
      color: ORGANIC_BAR_COLORS.left,
    },
  ];
  const paymentPreviewRecords = paymentRecords.slice(
    0,
    PAYMENT_RECORD_PREVIEW_LIMIT,
  );
  const openFullPaymentsList = () =>
    onViewAllPayments({
      records: paymentRecords,
      segments: paymentBreakdownSegments,
      leftToPay,
      patientResponsibility: expectedPatientResponsibility,
    });

  const summary = getCostStateSummary({
    state,
    treatmentLabel,
    estimateTotal: formatCurrency(adjustedTotal, locale),
    patientEstimate:
      patientEstimate != null ? formatCurrency(patientEstimate, locale) : null,
    quoteTotal:
      quoteSummaryAmount != null
        ? formatCurrency(quoteSummaryAmount, locale)
        : "No quote yet",
    quoteCount: quoteCards.length,
    comparableQuoteCount: uploadedQuoteCount,
    paymentDue: formatCurrency(leftToPay, locale),
    hasInsurance,
    coverageVerified,
    insuranceLabel: activeInsuranceLabel,
    clinicCount,
    verifiedClinicCount,
  });
  const summaryAmountValue =
    state === "clinic-estimate"
      ? (quoteSummaryAmount ?? 0)
      : state === "payments"
        ? leftToPay
        : (patientEstimate ?? adjustedTotal);
  const showHeroSummary =
    state !== "payments" &&
    !(state === "clinic-estimate" && quoteSummaryAmount == null);

  const runPrimaryAction = () => {
    if (state === "clinic-estimate" && quoteCards.length > 0) {
      onCompareQuotes({
        quotes: quoteCards,
        treatmentLabel,
        careLocation: selectedLocation.label,
        hasInsurance,
        verificationStatus,
        primaryPolicy,
        fertilityCoverage,
      });
      return;
    }
    if (summary.primaryRoute) {
      onNavigate(summary.primaryRoute);
      return;
    }
    onAddInsurance();
  };
  const PrimaryActionIcon =
    state === "clinic-estimate" && quoteCards.length > 0
      ? MessageCircle
      : Shield;
  const SecondaryActionIcon =
    summary.secondaryRoute === "Financing"
      ? CreditCard
      : summary.secondaryRoute === "TaxDeductions"
        ? Receipt
        : ChevronRight;

  const treatmentOptions = CONCEPT_TREATMENT_OPTIONS.map((treatment) => ({
    value: treatment,
    label: TREATMENT_LABELS[treatment],
  }));
  const locationSelectOptions = locationOptions.map((location) => ({
    value: location.id,
    label: location.label,
    detail: location.detail,
    disabled:
      location.action === "request-current-location" &&
      locationRequestStatus === "loading",
  }));
  // Bias the Places autocomplete toward where the person is so, for example,
  // typing "San" surfaces nearby cities first. Prefer their detected/precise
  // coordinates, then a coarse centroid from their profile state or ZIP; when
  // we have neither we omit the bias and let Google fall back to IP biasing.
  const locationBiasCenter = useMemo(() => {
    const detected = detectedLocation?.estimateLocation;
    if (detected?.latitude != null && detected?.longitude != null) {
      return { latitude: detected.latitude, longitude: detected.longitude };
    }
    const stateCode =
      normalizeUsStateCode(profileState) ??
      inferUsStateCodeFromZip(profileZipCode);
    return getUsStateCentroid(stateCode);
  }, [detectedLocation, profileState, profileZipCode]);
  // PillSelect already debounces its query, so search immediately here. The
  // control is only open when `openAssumptionControl === "location"`.
  const { predictions: placePredictions, isSearching: isSearchingPlaces } =
    usePlacesPredictions({
      query: locationQuery,
      minChars: 1,
      debounceMs: 0,
      regionsOnly: true,
      biasLocation: locationBiasCenter,
      enabled: openAssumptionControl === "location",
    });
  // While the person is searching, the dropdown shows live Places predictions
  // (prefixed so `handleSelectLocation` can tell them from the saved options);
  // otherwise it shows the suggested + saved locations.
  const placePredictionOptions = placePredictions.map((prediction) => ({
    value: `place:${prediction.placeId}`,
    label: prediction.structuredFormatting.mainText,
    detail: prediction.structuredFormatting.secondaryText || undefined,
  }));
  const locationDropdownOptions =
    locationQuery.trim().length > 0
      ? placePredictionOptions
      : locationSelectOptions;
  const handleLocationChange = async (nextLocationId: string) => {
    const nextLocation = locationOptions.find(
      (location) => location.id === nextLocationId,
    );

    if (nextLocation?.action !== "request-current-location") {
      setSelectedLocationId(nextLocationId);
      setLocationSearchHint(null);
      if (nextLocation) {
        captureLocationIntent({
          selectionSource: "suggested",
          requestedLocation: nextLocation.label,
          normalizedLocation: getEstimateAreaAnalyticsLabel(nextLocation),
          estimateLocation: getEstimateAreaAnalyticsLabel(nextLocation),
          matchType: nextLocation.estimateAreaLabel
            ? "nearest_fallback"
            : "exact",
        });
      }
      return;
    }

    setLocationRequestStatus("loading");
    try {
      const currentLocation = await requestCurrentEstimateAreaOption();
      setDetectedLocation(currentLocation);
      setSelectedLocationId(currentLocation.id);
      setLocationRequestStatus("idle");
      setLocationSearchHint(null);
      captureLocationIntent({
        selectionSource: "current_location",
        requestedLocation: currentLocation.label,
        normalizedLocation: getEstimateAreaAnalyticsLabel(currentLocation),
        estimateLocation: getEstimateAreaAnalyticsLabel(currentLocation),
        matchType: "exact",
      });
    } catch {
      setLocationRequestStatus("error");
    }
  };
  const applyPlaceResolution = (resolution: PlaceEstimateResolution) => {
    if (resolution.status === "unsupported") {
      // Keep the previous selection; a wrong-country estimate would be worse
      // than none. Surface why, and record the intent.
      setLocationSearchHint(resolution.hint);
    } else if (resolution.status === "exact") {
      setSelectedLocationId(resolution.option.id);
      setLocationSearchHint(null);
    } else {
      // nearest metro or national-country estimate: keep the entered location
      // in the list and select it.
      setCustomLocation(resolution.option);
      setSelectedLocationId(resolution.option.id);
      setLocationSearchHint(null);
    }

    const matchType =
      resolution.status === "exact"
        ? "exact"
        : resolution.status === "nearest"
          ? "nearest_fallback"
          : resolution.status === "country"
            ? "country_estimate"
            : "unsupported";
    captureLocationIntent({
      selectionSource: "search",
      requestedLocation: resolution.requestedLabel,
      normalizedLocation:
        resolution.status === "unsupported" ? null : resolution.normalizedLabel,
      estimateLocation:
        resolution.status === "unsupported"
          ? null
          : getEstimateAreaAnalyticsLabel(resolution.option),
      matchType,
    });
  };
  const handleLocationSearch = (query: string) => {
    setLocationQuery(query);
  };
  const handleSelectLocation = async (value: string) => {
    if (!value.startsWith("place:")) {
      await handleLocationChange(value);
      return;
    }
    const placeId = value.slice("place:".length);
    const prediction = placePredictions.find(
      (item) => item.placeId === placeId,
    );
    const details = await getPlaceDetails(placeId);
    const area = details ? extractPlaceArea(details) : null;
    if (!area) {
      setLocationSearchHint(
        "We couldn't read that location. Try another city, state, or country.",
      );
      return;
    }
    applyPlaceResolution(
      resolvePlaceEstimateArea({
        granularity: area.granularity,
        label: prediction?.structuredFormatting.mainText ?? area.label,
        city: area.city,
        stateCode: area.stateCode,
        countryCode: area.countryCode,
        countryName: area.countryName,
        latitude: area.latitude,
        longitude: area.longitude,
      }),
    );
  };
  const { mutateAsync: persistQuoteExtraction } = updateClinicQuoteMutation;
  const runQuoteExtraction = useCallback(
    async (quoteId: string, uploadId: string | null | undefined) => {
      if (!uploadId) {
        setQuoteExtractions((prev) => ({
          ...prev,
          [quoteId]: {
            status: "error",
            error: "Upload didn't complete, so the quote can't be analyzed.",
          },
        }));
        return;
      }
      setQuoteExtractions((prev) => ({
        ...prev,
        [quoteId]: { status: "extracting" },
      }));

      // Retry transient processing failures before surfacing "couldn't read".
      const maxAttempts = 3;
      let lastError: unknown;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const outcome = await transfer.processSource(
            {
              file: {
                uri: "",
                name: `clinic-quote-${quoteId}`,
                mimeType: "application/pdf",
              },
              uploadId,
            },
            {
              attempts: 24,
              intervalMs: 1_250,
            },
          );
          const quote = projectDocumentProcessingRunToPrefill(
            outcome.run,
            `clinic-quote-${quoteId}`,
          ).quote;
          const data: QuoteOCRResult = {
            clinicName: quote.clinicName || null,
            quoteDate: quote.quoteDate || null,
            currency: quote.currency || null,
            totalAmount: quote.totalAmount || null,
            lineItems: quote.lineItems.map((line) => ({
              label: line.label || null,
              amount: line.amount || null,
              category: line.category || null,
              notes: line.notes || null,
            })),
          };
          // When the call succeeds but yields nothing usable, log shape only (no
          // document content) so we can tell "backend returned no line items"
          // apart from a network/auth failure when triaging "couldn't read".
          const lineItemCount = Array.isArray(data?.lineItems)
            ? data.lineItems.length
            : null;
          if (!lineItemCount) {
            console.warn("[QuoteOCR] extraction returned no line items", {
              hasData: !!data,
              lineItemCount,
              hasTotal: !!data?.totalAmount,
            });
          }
          // The OCR result can omit fields at runtime; normalize to the
          // session shape before saving it on the clinic-quote record.
          const extraction: ClinicQuoteExtraction = {
            clinicName: data?.clinicName ?? null,
            quoteDate: data?.quoteDate ?? null,
            currency: data?.currency ?? null,
            totalAmount: data?.totalAmount ?? null,
            lineItems: (data?.lineItems ?? []).map((line) => ({
              label: line?.label ?? null,
              amount: line?.amount ?? null,
              category: line?.category ?? null,
              notes: line?.notes ?? null,
            })),
          };
          await persistQuoteExtraction({
            clinicQuoteId: quoteId,
            updates: { extraction },
          });
          setQuoteExtractions((prev) => ({
            ...prev,
            [quoteId]: { status: "done", data },
          }));
          return;
        } catch (err) {
          lastError = err;
          if (attempt < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, 400 * attempt));
          }
        }
      }
      console.warn("[QuoteOCR] extraction failed", lastError);
      setQuoteExtractions((prev) => ({
        ...prev,
        [quoteId]: {
          status: "error",
          error:
            lastError instanceof Error ? lastError.message : "Analysis failed",
        },
      }));
    },
    [persistQuoteExtraction, transfer],
  );

  // Session quotes that have not received an extraction are analyzed once;
  // the ref keeps re-renders from re-triggering the same quote.
  useEffect(() => {
    for (const quote of clinicQuotesQuery.data ?? []) {
      if (quote.extraction || !quote.uploadId) continue;
      if (attemptedQuoteExtractionsRef.current.has(quote.id)) continue;
      attemptedQuoteExtractionsRef.current.add(quote.id);
      void runQuoteExtraction(quote.id, quote.uploadId);
    }
  }, [clinicQuotesQuery.data, runQuoteExtraction]);

  const retryQuoteExtraction = (quoteId: string) => {
    const quote = savedClinicQuotes.find(
      (candidate) => candidate.id === quoteId,
    );
    if (!quote) return;
    attemptedQuoteExtractionsRef.current.delete(quoteId);
    void runQuoteExtraction(quoteId, quote.uploadId);
  };

  const quoteDeleteTarget = quoteDeleteTargetId
    ? (savedClinicQuotes.find((quote) => quote.id === quoteDeleteTargetId) ??
      null)
    : null;
  const deleteQuote = async () => {
    if (!quoteDeleteTargetId) return;
    setQuoteDeleteError(null);
    try {
      await deleteClinicQuoteMutation.mutateAsync(quoteDeleteTargetId);
      setExpandedQuoteIds((ids) =>
        ids.filter((id) => id !== quoteDeleteTargetId),
      );
      attemptedQuoteExtractionsRef.current.delete(quoteDeleteTargetId);
      setQuoteExtractions((current) => {
        const next = { ...current };
        delete next[quoteDeleteTargetId];
        return next;
      });
      setQuoteDeleteTargetId(null);
    } catch (error) {
      setQuoteDeleteError(
        error instanceof Error
          ? error.message
          : "We couldn't delete this quote. Try again.",
      );
    }
  };

  // Only domain data enters the session cache — display copy is derived at
  // render time in apiToUiPaymentRecord. The status is workflow state, so it's
  // set here once.
  const buildManualPaymentRecordPayload = (
    entry: ManualPaymentEntry,
  ): CreatePaymentRecordPayload => {
    const status: PaymentRecordStatus =
      entry.kind === "payment"
        ? "paid"
        : entry.kind === "claim"
          ? coverageVerified
            ? "waiting-eob"
            : "submitted"
          : entry.kind === "receipt"
            ? "needs-receipt"
            : "due";

    if (entry.attachmentName && !entry.attachmentDocumentId) {
      throw new Error("Attached payment document is missing its document ID");
    }

    return {
      kind: entry.kind,
      entrySource: "manual",
      label: entry.label,
      provider: entry.provider,
      amount: entry.amount,
      billedAmount: entry.amount,
      status,
      occurredOn: parseDateText(entry.date) ?? todayIsoDate(),
      note: entry.note || undefined,
      documents: entry.attachmentName
        ? [
            {
              id: entry.attachmentDocumentId as string,
              label: entry.attachmentName,
              status: "attached" as const,
              uploadId: entry.attachmentUploadId ?? null,
            },
          ]
        : [],
    };
  };

  const buildUploadedPaymentRecordPayload = (
    document: UploadedCostDocument,
  ): CreatePaymentRecordPayload => {
    if (!document.documentId || !document.uploadId) {
      throw new Error("Uploaded document is missing canonical linkage");
    }
    const kind = document.paymentKind ?? "payment";
    const status: PaymentRecordStatus =
      kind === "payment"
        ? "paid"
        : kind === "claim"
          ? coverageVerified
            ? "waiting-eob"
            : "submitted"
          : kind === "receipt"
            ? "needs-receipt"
            : "due";

    return {
      kind,
      entrySource: "document_upload",
      label: `Uploaded ${kind} document`,
      provider:
        document.source === "camera" ? "Uploaded photo" : "Uploaded file",
      amount: 0,
      billedAmount: 0,
      status,
      occurredOn: todayIsoDate(),
      documents: [
        toAttachedCostDocumentReference({
          documentId: document.documentId,
          uploadId: document.uploadId,
          name: document.name,
        }),
      ],
    };
  };

  const handleCostDocumentPicked = async (document: UploadedCostDocument) => {
    if (document.mode === "quote") {
      if (!document.uploadId) {
        throw new Error("Uploaded quote is missing its upload ID");
      }
      try {
        // A completed create can be committed server-side even if the client
        // loses the response. Refresh first so retrying the same upload does
        // not create a second quote.
        const refreshedQuotes = await clinicQuotesQuery.refetch();
        const existing = refreshedQuotes.data?.find(
          (quote) => quote.uploadId === document.uploadId,
        );
        const created =
          existing ??
          (await createClinicQuoteMutation.mutateAsync({
            name: document.name,
            uploadId: document.uploadId,
            mimeType: document.mimeType,
          }));
        setExpandedQuoteIds((ids) => Array.from(new Set([created.id, ...ids])));
        if (!created.extraction) {
          attemptedQuoteExtractionsRef.current.add(created.id);
          void runQuoteExtraction(created.id, created.uploadId);
        }
      } catch (err) {
        console.error("[Costs] failed to save clinic quote", err);
        throw err;
      }
      return;
    }

    if (!document.uploadId) {
      throw new Error("Uploaded payment is missing its upload ID");
    }
    const uploadId = document.uploadId;
    const refreshedRecords = await paymentRecordsQuery.refetch();
    const existing = refreshedRecords.data?.find((record) =>
      hasCostDocumentUpload(record.documents ?? [], uploadId),
    );
    const created =
      existing ??
      (await createPaymentRecordMutation.mutateAsync(
        buildUploadedPaymentRecordPayload(document),
      ));
    setSelectedPaymentRecordId(created.id);
  };
  const handleManualPaymentAdded = async (entry: ManualPaymentEntry) => {
    const refreshedRecords = entry.attachmentUploadId
      ? await paymentRecordsQuery.refetch()
      : null;
    const existing = entry.attachmentUploadId
      ? refreshedRecords?.data?.find((record) =>
          hasCostDocumentUpload(
            record.documents ?? [],
            entry.attachmentUploadId as string,
          ),
        )
      : undefined;
    const created =
      existing ??
      (await createPaymentRecordMutation.mutateAsync(
        buildManualPaymentRecordPayload(entry),
      ));
    setSelectedPaymentRecordId(created.id);
  };
  const handlePaymentRecordSaved = async (record: CostPaymentRecordParam) => {
    const updated = await updatePaymentRecordMutation.mutateAsync({
      paymentRecordId: record.id,
      updates: uiToPaymentRecordUpdates(record),
    });
    const mapped = apiToUiPaymentRecord(updated, locale);
    setEditedPaymentRecords((records) => ({
      ...records,
      [record.id]: mapped as PaymentRecord,
    }));
    setSelectedPaymentRecordId(record.id);
  };
  const attachPaymentDocument = async (document: UploadedCostDocument) => {
    if (!attachmentRecordId) throw new Error("No payment record selected");
    const record = paymentRecords.find(
      (candidate) => candidate.id === attachmentRecordId,
    );
    if (!record) throw new Error("Payment record is no longer available");
    if (!document.documentId || !document.uploadId) {
      throw new Error("Uploaded document is missing canonical linkage");
    }

    const attached = toAttachedCostDocumentReference({
      documentId: document.documentId,
      uploadId: document.uploadId,
      name: document.name,
    });
    const documents = mergeCostDocumentReferences(record.documents, attached);
    const savedRecord = savedPaymentRecords.find(
      (candidate) => candidate.id === record.id,
    );
    if (savedRecord) {
      await updatePaymentRecordMutation.mutateAsync({
        paymentRecordId: record.id,
        updates: { documents },
      });
    } else {
      setPaymentDocumentAttachments((attachments) => ({
        ...attachments,
        [record.id]: [
          {
            id: attached.id,
            label: document.name,
            detail: "Attached to this ledger item",
            status: "attached" as const,
            uploadId: attached.uploadId,
          },
          ...(attachments[record.id] ?? []),
        ],
      }));
    }
    setAttachmentRecordId(null);
  };
  const deletePaymentRecord = async (recordId: string) => {
    await deletePaymentRecordMutation.mutateAsync(recordId);
    setDeletedPaymentRecordIds((ids) =>
      Array.from(new Set([...ids, recordId])),
    );
    setSelectedPaymentRecordId(null);
  };
  const toggleQuoteExpanded = (quoteId: string) => {
    setExpandedQuoteIds((ids) =>
      ids.includes(quoteId)
        ? ids.filter((id) => id !== quoteId)
        : [...ids, quoteId],
    );
  };
  const toggleLine = (key: string) => {
    setSelectedLineKeys((keys) =>
      keys.includes(key)
        ? keys.filter((lineKey) => lineKey !== key)
        : [...keys, key],
    );
  };
  const toggleSection = (section: EstimateScenarioSection) => {
    const sectionKeys = section.lines.map((line) => line.key);
    const allSelected = sectionKeys.every((key) => selectedLineKeySet.has(key));

    setSelectedLineKeys((keys) => {
      if (allSelected) {
        return keys.filter((key) => !sectionKeys.includes(key));
      }

      return Array.from(new Set([...keys, ...sectionKeys]));
    });
  };
  const setAssumptionControlOpen = (
    control: "treatment" | "location",
    open: boolean,
  ) => {
    if (open) {
      estimateFlow.stepViewed(control, { treatment: selectedTreatment });
      setOpenAssumptionControl(control);
      setShowBreakdown(false);
      return;
    }
    setOpenAssumptionControl((current) =>
      current === control ? null : current,
    );
  };
  const toggleScenarioBuilder = () => {
    if (!showBreakdown) {
      estimateFlow.stepViewed("breakdown", { treatment: selectedTreatment });
    }
    setOpenAssumptionControl(null);
    setShowBreakdown((value) => !value);
  };
  const renderAssumptionDivider = (visible: boolean) =>
    visible ? (
      <View
        style={{
          width: 1,
          height: 22,
          backgroundColor: "rgba(42, 37, 34, 0.1)",
        }}
      />
    ) : null;

  const renderAssumptions = ({
    compact = false,
  }: { compact?: boolean } = {}) => {
    const usesControlBar = compact && topControlsVariant === "bar";

    return (
      <View
        style={{
          flexDirection: "row",
          flexWrap: compact ? "nowrap" : "wrap",
          justifyContent: compact ? "center" : "flex-end",
          gap: usesControlBar ? 0 : compact ? 6 : 8,
          alignItems: compact ? "center" : "flex-start",
          zIndex: 20,
          width: compact ? "100%" : undefined,
          ...(usesControlBar
            ? {
                minHeight: 38,
                borderRadius: 999,
                borderWidth: 1,
                borderColor: "rgba(42, 37, 34, 0.055)",
                backgroundColor: "rgba(42, 37, 34, 0.025)",
                padding: 3,
              }
            : null),
        }}
      >
        <View
          style={
            usesControlBar
              ? {
                  flexGrow: 1,
                  flexShrink: 1,
                  flexBasis: 0,
                  minWidth: 0,
                }
              : compact
                ? {
                    flexGrow: 1,
                    flexShrink: 1,
                    flexBasis: 0,
                    maxWidth: 205,
                    minWidth: 0,
                  }
                : undefined
          }
        >
          <PillSelect
            value={selectedTreatment}
            options={treatmentOptions}
            onChange={(next) => {
              estimateFlow.stepSubmitted("treatment", { treatment: next });
              setSelectedTreatment(next as TreatmentType);
            }}
            icon={ClipboardList}
            accessibilityLabel="Treatment"
            compact={compact}
            surface={usesControlBar ? "bar" : "pill"}
            open={openAssumptionControl === "treatment"}
            onOpenChange={(open) => setAssumptionControlOpen("treatment", open)}
            style={compact ? { width: "100%" } : { maxWidth: 210 }}
          />
        </View>
        {renderAssumptionDivider(usesControlBar)}
        <View
          style={
            usesControlBar
              ? {
                  flexGrow: 0,
                  flexShrink: 0,
                  flexBasis: 92,
                  width: 92,
                }
              : compact
                ? {
                    flexGrow: 0,
                    flexShrink: 0,
                    flexBasis: 92,
                    width: 92,
                  }
                : undefined
          }
        >
          <PillSelect
            value={selectedLocation.id}
            options={locationDropdownOptions}
            onChange={handleSelectLocation}
            icon={MapPin}
            accessibilityLabel="Location"
            placeholder={selectedLocation.label}
            searchable
            searchPlaceholder="Search any city, state, or country"
            onSearch={handleLocationSearch}
            searching={isSearchingPlaces}
            filterOptionsLocally={false}
            compact={compact}
            surface={usesControlBar ? "bar" : "pill"}
            open={openAssumptionControl === "location"}
            onOpenChange={(open) => {
              setAssumptionControlOpen("location", open);
              // Clearing the query resets predictions via the hook (which also
              // idles once the control is no longer the open assumption).
              if (!open) setLocationQuery("");
            }}
            style={compact ? { width: "100%" } : { maxWidth: 210 }}
          />
        </View>
        {renderAssumptionDivider(usesControlBar)}
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(showBreakdown ? "Hide scenario" : "Scenario builder"),
              showBreakdown ? "hide_scenario" : "open_scenario_builder",
            );
            toggleScenarioBuilder();
          }}
          accessibilityRole="button"
          accessibilityLabel={
            showBreakdown ? "Hide scenario" : "Scenario builder"
          }
          style={{
            minHeight: compact ? 32 : 36,
            flexGrow: compact ? 0 : undefined,
            flexShrink: compact ? 0 : undefined,
            flexBasis: compact ? (usesControlBar ? 174 : 178) : undefined,
            width: compact ? (usesControlBar ? 174 : 178) : undefined,
            minWidth: compact ? (usesControlBar ? 174 : 178) : 0,
            maxWidth: compact ? undefined : 210,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: compact ? 5 : 6,
            paddingLeft: compact ? 10 : 11,
            paddingRight: compact ? 8 : 9,
            paddingVertical: compact ? 6 : 7,
            borderRadius: 999,
            borderWidth: usesControlBar ? 0 : showBreakdown ? 1 : 0,
            borderColor: "rgba(42, 37, 34, 0.12)",
            backgroundColor: usesControlBar
              ? "transparent"
              : showBreakdown
                ? "rgba(42, 37, 34, 0.11)"
                : compact
                  ? "rgba(42, 37, 34, 0.065)"
                  : "rgba(42, 37, 34, 0.08)",
          }}
        >
          <SlidersHorizontal
            size={compact ? 12 : 13}
            color={
              usesControlBar && showBreakdown
                ? EMBER.colors.text
                : EMBER.colors.textLight
            }
            strokeWidth={usesControlBar && showBreakdown ? 2 : 1.8}
          />
          <Text
            className="font-dm-sans"
            numberOfLines={1}
            style={{
              flexShrink: 1,
              minWidth: 0,
              fontSize: compact ? 12 : 13,
              fontWeight: usesControlBar && showBreakdown ? "800" : "700",
              color: EMBER.colors.text,
            }}
          >
            {showBreakdown ? "Hide scenario" : "Scenario builder"}
          </Text>
          {showBreakdown ? (
            <Minus
              size={compact ? 12 : 13}
              color={
                usesControlBar && showBreakdown
                  ? EMBER.colors.text
                  : EMBER.colors.textLight
              }
              strokeWidth={usesControlBar && showBreakdown ? 2.2 : 2}
              style={{ marginLeft: "auto" }}
            />
          ) : (
            <Plus
              size={compact ? 12 : 13}
              color={EMBER.colors.textLight}
              strokeWidth={2}
              style={{ marginLeft: "auto" }}
            />
          )}
        </Pressable>
      </View>
    );
  };

  const renderScenarioControls = () => (
    <View
      style={{
        borderRadius: 16,
        borderWidth: 1,
        borderColor: "rgba(42, 37, 34, 0.08)",
        backgroundColor: "rgba(255, 255, 255, 0.22)",
        overflow: "hidden",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: 14,
          paddingBottom: 12,
        }}
      >
        <View style={{ gap: 2 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              fontWeight: "700",
              color: EMBER.colors.text,
            }}
          >
            Scenario assumptions
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              maxWidth: 310,
              fontSize: 12,
              lineHeight: 17,
              color: EMBER.colors.textLight,
            }}
          >
            {
              "These are starting assumptions. A clinic quote, pharmacy quote, or benefit check can replace them."
            }
          </Text>
        </View>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 12, color: EMBER.colors.textLight }}
        >
          {activeLineKeys.length} selected
        </Text>
      </View>

      <View style={{ gap: 10, paddingHorizontal: 14, paddingBottom: 14 }}>
        {estimateSections.some((section) =>
          section.lines.some((line) => line.multiplier === "cycle"),
        ) ? (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, color: EMBER.colors.textLight }}
            >
              Treatment cycles
            </Text>
            <MoonStepper
              value={cycleCount}
              onChange={setCycleCount}
              min={1}
              max={4}
              accentColor={EMBER.colors.textLight}
            />
          </View>
        ) : null}

        {hasMedicationEstimate(selectedTreatment) ? (
          <View style={{ gap: 7 }}>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, color: EMBER.colors.textLight }}
            >
              Medication intensity
            </Text>
            <View style={{ flexDirection: "row", gap: 6 }}>
              {(["low", "typical", "high"] as const).map((option) => {
                const selected = medicationIntensity === option;
                return (
                  <Pressable
                    key={option}
                    onPress={() => {
                      capturePress(pressLabel(option), "medication_intensity");
                      setMedicationIntensity(option);
                    }}
                    style={{
                      flex: 1,
                      minHeight: 30,
                      borderRadius: 999,
                      borderWidth: 1,
                      borderColor: selected
                        ? "rgba(42, 37, 34, 0.24)"
                        : "rgba(42, 37, 34, 0.08)",
                      backgroundColor: selected
                        ? "rgba(42, 37, 34, 0.1)"
                        : "rgba(255, 255, 255, 0.18)",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 12,
                        fontWeight: selected ? "700" : "600",
                        color: selected
                          ? EMBER.colors.text
                          : EMBER.colors.textLight,
                        textTransform: "capitalize",
                      }}
                    >
                      {option}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        ) : null}

        {estimateSections.some((section) =>
          section.lines.some((line) => line.multiplier === "embryo"),
        ) ? (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, color: EMBER.colors.textLight }}
            >
              Embryos for PGT-A
            </Text>
            <MoonStepper
              value={embryoCount}
              onChange={setEmbryoCount}
              min={1}
              max={12}
              accentColor={EMBER.colors.textLight}
            />
          </View>
        ) : null}

        {estimateSections.some((section) =>
          section.lines.some((line) => line.multiplier === "storage-year"),
        ) ? (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, color: EMBER.colors.textLight }}
            >
              Storage years
            </Text>
            <MoonStepper
              value={storageYears}
              onChange={setStorageYears}
              min={1}
              max={10}
              accentColor={EMBER.colors.textLight}
            />
          </View>
        ) : null}

        {hasTransferPath(selectedTreatment) ? (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, color: EMBER.colors.textLight }}
            >
              Transfer cycles
            </Text>
            <MoonStepper
              value={transferCount}
              onChange={setTransferCount}
              min={1}
              max={4}
              accentColor={EMBER.colors.textLight}
            />
          </View>
        ) : null}
      </View>

      <View
        style={{
          borderTopWidth: 1,
          borderTopColor: "rgba(42, 37, 34, 0.08)",
        }}
      >
        {estimateSections.map(renderEstimateSection)}
      </View>

      <View
        style={{
          borderTopWidth: 1,
          borderTopColor: "rgba(42, 37, 34, 0.08)",
          padding: 12,
        }}
      >
        {renderInsuranceEstimateState()}
      </View>
    </View>
  );

  const renderEstimateSection = (
    section: EstimateScenarioSection,
    index: number,
  ) => {
    const selectedCount = section.lines.filter((line) =>
      selectedLineKeySet.has(line.key),
    ).length;
    const separateLineCount = section.lines.filter(
      (line) => line.status === "often-separate",
    ).length;
    const sectionTotal = section.lines.reduce(
      (sum, line) =>
        selectedLineKeySet.has(line.key)
          ? sum +
            getLineAmount(line, {
              cycleCount,
              embryoCount,
              storageYears,
              transferCount,
            })
          : sum,
      0,
    );
    const allSelected = selectedCount === section.lines.length;

    return (
      <View
        key={section.key}
        style={{
          borderTopWidth: index > 0 ? 1 : 0,
          borderTopColor: "rgba(42, 37, 34, 0.07)",
        }}
      >
        <Pressable
          onPress={() => {
            capturePress(pressLabel(section.title), "estimate_section_row");
            toggleSection(section);
          }}
          style={{
            paddingHorizontal: 14,
            paddingTop: 13,
            paddingBottom: 10,
            flexDirection: "row",
            alignItems: "flex-start",
            gap: 10,
          }}
        >
          <View style={{ marginTop: 1 }}>
            <EstimateStateIcon selected={allSelected} />
          </View>
          <View style={{ flex: 1, gap: 3 }}>
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  fontSize: 13,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                }}
              >
                {section.title}
              </Text>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <Text
                  className="font-dm-mono"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.text,
                    textAlign: "right",
                  }}
                >
                  {formatCurrency(sectionTotal, locale)}
                </Text>
                <EstimateCheckbox selected={allSelected} />
              </View>
            </View>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 17,
                color: EMBER.colors.textLight,
              }}
            >
              {section.description}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                color: EMBER.colors.textFaint,
              }}
            >
              {selectedCount}/{section.lines.length} selected
              {separateLineCount > 0
                ? ` · ${separateLineCount} usually separate ${
                    separateLineCount === 1 ? "line" : "lines"
                  }`
                : ""}
            </Text>
          </View>
        </Pressable>

        <View style={{ paddingBottom: 8 }}>
          {section.lines.map((line, index) => {
            const selected = selectedLineKeySet.has(line.key);
            const statusColor = getStatusColor(line.status);
            return (
              <Pressable
                key={line.key}
                onPress={() => {
                  capturePress(pressLabel(line.label), "estimate_line_row");
                  toggleLine(line.key);
                }}
                style={{
                  marginLeft: 40,
                  marginRight: 12,
                  paddingLeft: 12,
                  paddingRight: 2,
                  paddingVertical: 10,
                  borderLeftWidth: 1,
                  borderLeftColor: "rgba(42, 37, 34, 0.12)",
                  borderTopWidth: index > 0 ? 1 : 0,
                  borderTopColor: "rgba(42, 37, 34, 0.055)",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 10,
                  opacity: selected ? 1 : 0.58,
                }}
              >
                <EstimateStateIcon selected={selected} />
                <View style={{ flex: 1, gap: 4 }}>
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 10,
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        flex: 1,
                        fontSize: 12,
                        fontWeight: "600",
                        color: EMBER.colors.text,
                        lineHeight: 17,
                      }}
                    >
                      {line.label}
                    </Text>
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <Text
                        className="font-dm-mono"
                        style={{
                          fontSize: 12,
                          fontWeight: "700",
                          color: EMBER.colors.text,
                          textAlign: "right",
                        }}
                      >
                        {formatCurrency(
                          getLineAmount(line, {
                            cycleCount,
                            embryoCount,
                            storageYears,
                            transferCount,
                          }),
                          locale,
                        )}
                      </Text>
                      <EstimateCheckbox selected={selected} />
                    </View>
                  </View>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 11,
                      lineHeight: 16,
                      color: EMBER.colors.textLight,
                    }}
                  >
                    {line.detail}
                  </Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      alignSelf: "flex-start",
                      fontSize: 10,
                      fontWeight: "700",
                      color: statusColor,
                    }}
                  >
                    {getStatusLabel(line.status)}
                  </Text>
                </View>
              </Pressable>
            );
          })}
        </View>
      </View>
    );
  };

  const renderInsuranceEstimateState = () => (
    <InsightBox
      icon={Shield}
      title={hasInsurance ? "Insurance estimate" : "Insurance not connected"}
      body={
        hasInsurance
          ? coverageVerified
            ? "Verified benefits can be applied line by line, but clinic fee schedules and network rules still affect the final patient responsibility."
            : "We have insurance on file, but benefits are not verified yet. Until then, this remains a before-insurance estimate."
          : "Add insurance to estimate deductible, coinsurance, exclusions, lifetime maximums, and which lines are likely self-pay."
      }
    />
  );

  const renderEstimateDetails = () => (
    <Collapsible expanded={showBreakdown}>
      <View style={{ paddingTop: 12 }}>{renderScenarioControls()}</View>
    </Collapsible>
  );

  const getQuoteBadgeTone = (
    tone: ClinicQuote["statusTone"],
  ): StatusBadgeTone => {
    if (tone === "ready") return "green";
    if (tone === "attention") return "gold";
    return "neutral";
  };

  const getPaymentToneStyle = (status: PaymentRecord["status"]) => {
    if (status === "paid") {
      return {
        label: "Paid",
        color: EMBER.colors.green,
        badgeTone: "green" as StatusBadgeTone,
      };
    }
    if (status === "submitted") {
      return {
        label: "Submitted",
        color: colors.greyBrown,
        badgeTone: "blue" as StatusBadgeTone,
      };
    }
    if (status === "waiting-eob") {
      return {
        label: "Waiting for EOB",
        color: colors.greyBrown,
        badgeTone: "neutral" as StatusBadgeTone,
      };
    }
    if (status === "needs-receipt") {
      return {
        label: "Needs receipt",
        color: EMBER.colors.gold,
        badgeTone: "gold" as StatusBadgeTone,
      };
    }
    if (status === "partially-covered") {
      return {
        label: "Partially covered",
        color: EMBER.colors.gold,
        badgeTone: "gold" as StatusBadgeTone,
      };
    }
    if (status === "ready-reimbursement") {
      return {
        label: "Ready for claim",
        color: colors.greyBrown,
        badgeTone: "green" as StatusBadgeTone,
      };
    }
    if (status === "denied") {
      return {
        label: "Denied",
        color: EMBER.colors.coral,
        badgeTone: "coral" as StatusBadgeTone,
      };
    }
    if (status === "review") {
      return {
        label: "Needs match",
        color: EMBER.colors.gold,
        badgeTone: "gold" as StatusBadgeTone,
      };
    }
    return {
      label: "Left to pay",
      color: EMBER.colors.coral,
      badgeTone: "coral" as StatusBadgeTone,
    };
  };

  const shouldShowPaymentStatusBadge = (status: PaymentRecord["status"]) =>
    status === "submitted" ||
    status === "waiting-eob" ||
    status === "denied" ||
    status === "due" ||
    status === "review";

  const renderStatusBadge = (label: string, tone: StatusBadgeTone) => (
    <View style={{ alignSelf: "flex-start" }}>
      <StatusBadge label={label} tone={tone} />
    </View>
  );

  const renderDocumentNeededBadge = () => (
    <StatusBadge
      label="Document needed"
      tone="gold"
      icon={FileQuestion}
      iconOnly
      accessibilityLabel="Document needed"
    />
  );

  const getPaymentRecordIcon = (record: PaymentRecord) => {
    const searchable =
      `${record.label} ${record.source} ${record.phase}`.toLowerCase();

    if (
      searchable.includes("claim") ||
      searchable.includes("insurance") ||
      searchable.includes("eob")
    ) {
      return FileCheck2;
    }

    if (
      searchable.includes("medication") ||
      searchable.includes("pharmacy") ||
      searchable.includes("receipt") ||
      searchable.includes("invoice")
    ) {
      return Receipt;
    }

    if (
      searchable.includes("deposit") ||
      searchable.includes("balance") ||
      record.status === "due"
    ) {
      return Wallet;
    }

    return FileText;
  };

  const renderMiniStat = (label: string, value: string, detail: string) => (
    <View
      style={{
        flex: 1,
        minWidth: 0,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: "rgba(42, 37, 34, 0.08)",
        backgroundColor: "rgba(255, 255, 255, 0.2)",
        padding: 11,
        gap: 3,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 10,
          fontWeight: "700",
          color: EMBER.colors.textLight,
        }}
      >
        {label}
      </Text>
      <Text
        className="font-dm-mono"
        style={{ fontSize: 15, fontWeight: "800", color: EMBER.colors.text }}
      >
        {value}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 10, lineHeight: 14, color: EMBER.colors.textFaint }}
      >
        {detail}
      </Text>
    </View>
  );

  const getQuoteConcern = (line: QuoteLineItem) => {
    const detail = line.detail.toLowerCase();
    if (detail.includes("optional") || detail.includes("not included")) {
      return "Optional or not included";
    }
    if (detail.includes("separate") || detail.includes("provider")) {
      return "May be billed separately";
    }
    if (detail.includes("confirm") || detail.includes("verify")) {
      return "Needs clinic confirmation";
    }
    return null;
  };

  const renderQuoteFact = (label: string, value: string, detail: string) => (
    <View
      style={{
        flex: 1,
        minWidth: 100,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: "rgba(42, 37, 34, 0.07)",
        backgroundColor: "rgba(255, 255, 255, 0.2)",
        padding: 10,
        gap: 3,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{ fontSize: 10, color: EMBER.colors.textLight }}
      >
        {label}
      </Text>
      <Text
        className="font-dm-mono"
        style={{ fontSize: 13, fontWeight: "800", color: EMBER.colors.text }}
      >
        {value}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 10, lineHeight: 14, color: EMBER.colors.textFaint }}
      >
        {detail}
      </Text>
    </View>
  );

  const renderQuoteLineItem = (line: QuoteLineItem) => (
    <View
      key={line.key}
      style={{
        borderTopWidth: 1,
        borderTopColor: "rgba(42, 37, 34, 0.06)",
        paddingTop: 11,
        gap: 7,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <View style={{ flex: 1, minWidth: 0, gap: 3 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              fontWeight: "700",
              color: EMBER.colors.text,
            }}
          >
            {line.label}
          </Text>
          {line.category ? (
            <Text
              className="font-dm-sans"
              style={{ fontSize: 10, color: EMBER.colors.textFaint }}
            >
              {line.category}
            </Text>
          ) : null}
        </View>
        <Text
          className="font-dm-mono"
          style={{
            fontSize: 13,
            fontWeight: "800",
            color: EMBER.colors.text,
          }}
        >
          {formatCurrency(line.amount, locale)}
        </Text>
      </View>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 11, lineHeight: 16, color: EMBER.colors.textMid }}
      >
        {line.detail}
      </Text>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
        <StatusBadge label="From document" tone="neutral" />
        <StatusBadge
          label={
            coverageVerified ? "Coverage verified" : "Coverage not checked"
          }
          tone={coverageVerified ? "green" : "gold"}
        />
      </View>
    </View>
  );

  const renderQuoteCard = (quote: ClinicQuote) => {
    const expanded = expandedQuoteIds.includes(quote.id);
    const tone = getQuoteBadgeTone(quote.statusTone);
    const hasComparableAmount = quote.statusTone === "ready";
    const devQuoteDeleteFixture =
      __DEV__ &&
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get(
        "dev_costs_quote_delete",
      ) === "1";
    const canDelete =
      clinicQuotesQuery.data?.some(
        (savedQuote) => savedQuote.id === quote.id,
      ) || devQuoteDeleteFixture;
    const quoteConcerns = quote.lineItems.flatMap((line) => {
      const concern = getQuoteConcern(line);
      return concern ? [{ line, concern }] : [];
    });

    return (
      <View
        key={quote.id}
        style={{
          borderRadius: 18,
          borderWidth: 1,
          borderColor: "rgba(42, 37, 34, 0.085)",
          backgroundColor: "rgba(255, 255, 255, 0.24)",
          overflow: "hidden",
        }}
      >
        <Pressable
          onPress={() => {
            capturePress(pressLabel(quote.clinic), "clinic_quote_row");
            toggleQuoteExpanded(quote.id);
          }}
          style={{ padding: 13, gap: 10 }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 10,
            }}
          >
            <View
              style={{
                width: 40,
                height: 40,
                borderRadius: 20,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "transparent",
                borderWidth: 1,
                borderColor: "rgba(107, 85, 72, 0.12)",
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  fontWeight: "800",
                  color: colors.greyBrown,
                }}
              >
                {quote.logoText}
              </Text>
            </View>
            <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  fontSize: 14,
                  fontWeight: "800",
                  color: EMBER.colors.text,
                }}
              >
                {quote.clinic}
              </Text>
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{ fontSize: 11, color: EMBER.colors.textLight }}
              >
                {quote.location} · {quote.documentLabel}
              </Text>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                {renderStatusBadge(quote.status, tone)}
                <Text
                  className="font-dm-sans"
                  style={{
                    alignSelf: "center",
                    fontSize: 10,
                    color: EMBER.colors.textFaint,
                  }}
                >
                  {quote.uploadedAt}
                </Text>
              </View>
            </View>
            <View style={{ alignItems: "flex-end", gap: 3 }}>
              <Text
                className="font-dm-mono"
                style={{
                  fontSize: 16,
                  fontWeight: "800",
                  color: EMBER.colors.text,
                }}
              >
                {hasComparableAmount
                  ? formatCurrency(quote.quoteTotal, locale)
                  : "—"}
              </Text>
              <Text
                className="font-dm-sans"
                style={{ fontSize: 10, color: EMBER.colors.textLight }}
              >
                {hasComparableAmount
                  ? `${formatCurrency(quote.outOfPocket, locale)} OOP`
                  : "OOP unavailable"}
              </Text>
              <ChevronDown
                size={15}
                color={EMBER.colors.textFaint}
                strokeWidth={1.8}
                style={{
                  transform: [{ rotate: expanded ? "180deg" : "0deg" }],
                }}
              />
            </View>
          </View>
        </Pressable>

        <Collapsible expanded={expanded}>
          <View
            style={{
              borderTopWidth: 1,
              borderTopColor: "rgba(42, 37, 34, 0.07)",
              paddingHorizontal: 13,
              paddingBottom: 13,
              paddingTop: 13,
              gap: 16,
            }}
          >
            {quote.statusTone !== "ready" ? (
              <>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    lineHeight: 18,
                    color: EMBER.colors.textMid,
                  }}
                >
                  {quote.summary}
                </Text>
                <InsightBox
                  icon={Sparkles}
                  title={quote.analysisTitle}
                  body={quote.analysisBody}
                />
              </>
            ) : null}

            {quote.statusTone === "attention" ? (
              <Pressable
                onPress={() => {
                  capturePress(pressLabel("Try reading again"));
                  retryQuoteExtraction(quote.id);
                }}
                style={{
                  alignSelf: "flex-start",
                  minHeight: 34,
                  borderRadius: 999,
                  backgroundColor: "rgba(107, 85, 72, 0.1)",
                  paddingHorizontal: 13,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    fontWeight: "800",
                    color: colors.greyBrown,
                  }}
                >
                  Try reading again
                </Text>
              </Pressable>
            ) : null}

            {quote.statusTone === "ready" ? (
              <>
                <View style={{ gap: 9 }}>
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 7,
                    }}
                  >
                    <FileText
                      size={14}
                      color={EMBER.colors.textLight}
                      strokeWidth={1.7}
                    />
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 12,
                        fontWeight: "800",
                        color: EMBER.colors.text,
                      }}
                    >
                      Document facts
                    </Text>
                  </View>
                  <View
                    style={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    {renderQuoteFact(
                      "Quoted total",
                      formatCurrency(quote.quoteTotal, locale),
                      "From document",
                    )}
                    {renderQuoteFact(
                      "Quote date",
                      quote.quoteDate
                        ? formatDate(quote.quoteDate, locale)
                        : "Not listed",
                      "From document",
                    )}
                    {renderQuoteFact(
                      "Patient estimate",
                      formatCurrency(quote.outOfPocket, locale),
                      coverageVerified
                        ? "Plan-aware estimate"
                        : "Modeled until coverage is verified",
                    )}
                  </View>
                </View>

                {quoteConcerns.length > 0 ? (
                  <View
                    style={{
                      borderRadius: 14,
                      borderWidth: 1,
                      borderColor: "rgba(168, 86, 58, 0.18)",
                      backgroundColor: "rgba(168, 86, 58, 0.06)",
                      padding: 12,
                      gap: 9,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 7,
                      }}
                    >
                      <FileQuestion
                        size={14}
                        color={EMBER.colors.coral}
                        strokeWidth={1.7}
                      />
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 12,
                          fontWeight: "800",
                          color: EMBER.colors.text,
                        }}
                      >
                        Worth confirming with the clinic
                      </Text>
                    </View>
                    {quoteConcerns.map(({ line, concern }) => (
                      <View
                        key={`${quote.id}-${line.key}-concern`}
                        style={{ gap: 3 }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 11,
                            fontWeight: "800",
                            color: EMBER.colors.text,
                          }}
                        >
                          {line.label} · {concern}
                        </Text>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 11,
                            lineHeight: 16,
                            color: EMBER.colors.textMid,
                          }}
                        >
                          {line.detail}
                        </Text>
                      </View>
                    ))}
                  </View>
                ) : null}

                <View style={{ gap: 9 }}>
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 7,
                    }}
                  >
                    <FileCheck2
                      size={14}
                      color={EMBER.colors.textLight}
                      strokeWidth={1.7}
                    />
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 12,
                        fontWeight: "800",
                        color: EMBER.colors.text,
                      }}
                    >
                      Line items from the document
                    </Text>
                  </View>
                  <View style={{ gap: 10 }}>
                    {quote.lineItems.map((line) => renderQuoteLineItem(line))}
                  </View>
                </View>

                {!coverageVerified ? (
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "flex-start",
                      gap: 8,
                      borderRadius: 14,
                      backgroundColor: "rgba(176, 128, 48, 0.08)",
                      padding: 12,
                    }}
                  >
                    <Shield
                      size={15}
                      color={EMBER.colors.gold}
                      strokeWidth={1.7}
                    />
                    <View style={{ flex: 1, gap: 3 }}>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 11,
                          fontWeight: "800",
                          color: EMBER.colors.text,
                        }}
                      >
                        Coverage has not been checked against this quote
                      </Text>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 11,
                          lineHeight: 16,
                          color: EMBER.colors.textMid,
                        }}
                      >
                        The patient estimate is modeled, not a confirmed bill.
                      </Text>
                    </View>
                  </View>
                ) : null}
              </>
            ) : null}

            {canDelete ? (
              <Pressable
                testID="costs-quote-delete"
                onPress={() => {
                  capturePress(pressLabel("Delete quote"));
                  setQuoteDeleteError(null);
                  setQuoteDeleteTargetId(quote.id);
                }}
                disabled={deleteClinicQuoteMutation.isPending}
                style={{ alignSelf: "flex-start", paddingVertical: 4 }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    fontWeight: "800",
                    color: EMBER.colors.coral,
                  }}
                >
                  Delete quote
                </Text>
              </Pressable>
            ) : null}
          </View>
        </Collapsible>
      </View>
    );
  };

  const renderQuoteDetails = () => {
    const lowestOutOfPocket =
      comparableQuoteCards.length > 0
        ? Math.min(...comparableQuoteCards.map((quote) => quote.outOfPocket))
        : null;
    if (quoteCards.length === 0) {
      return (
        <View style={{ gap: 12 }}>
          <View
            style={{
              borderRadius: 18,
              borderWidth: 1,
              borderColor: "rgba(42, 37, 34, 0.08)",
              backgroundColor: "rgba(255, 255, 255, 0.22)",
              padding: 16,
              alignItems: "center",
              gap: 10,
            }}
          >
            <Image
              source={COST_QUOTE_RECEIPT_ILLUSTRATION}
              resizeMode="contain"
              style={{ width: 86, height: 72 }}
            />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                fontWeight: "800",
                color: EMBER.colors.text,
                textAlign: "center",
              }}
            >
              No clinic quotes yet
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                maxWidth: 310,
                fontSize: 12,
                lineHeight: 18,
                color: EMBER.colors.textMid,
                textAlign: "center",
              }}
            >
              Upload a quote or save one from a clinic conversation. Once it is
              itemized, Louise can show what is included, missing, covered, and
              financeable.
            </Text>
            <Pressable
              onPress={() => {
                capturePress(pressLabel("Upload quote"));
                setUploadModalMode("quote");
              }}
              style={{
                minHeight: 38,
                borderRadius: 999,
                backgroundColor: "rgba(107, 85, 72, 0.12)",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "row",
                gap: 7,
                paddingHorizontal: 14,
              }}
            >
              <UploadCloud
                size={14}
                color={colors.greyBrown}
                strokeWidth={1.8}
              />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  fontWeight: "800",
                  color: colors.greyBrown,
                }}
              >
                Upload quote
              </Text>
            </Pressable>
          </View>
        </View>
      );
    }

    return (
      <View style={{ gap: 12 }}>
        <View style={{ flexDirection: "row", gap: 8 }}>
          {renderMiniStat(
            "Lowest quote",
            quoteSummaryAmount != null
              ? formatCurrency(quoteSummaryAmount, locale)
              : "—",
            "Before insurance",
          )}
          {renderMiniStat(
            "Lowest OOP",
            lowestOutOfPocket != null
              ? formatCurrency(lowestOutOfPocket, locale)
              : "—",
            coverageVerified ? "Plan-aware" : "Modeled",
          )}
        </View>

        <View style={{ gap: 10 }}>{quoteCards.map(renderQuoteCard)}</View>
      </View>
    );
  };

  const renderQuoteUploadButton = () => (
    <Pressable
      onPress={() => {
        capturePress(pressLabel("Upload"));
        setUploadModalMode("quote");
      }}
      style={{
        position: "absolute",
        top: 12,
        right: 18,
        zIndex: 20,
        minHeight: 34,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: "rgba(42, 37, 34, 0.1)",
        backgroundColor: "rgba(255, 255, 255, 0.28)",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "row",
        gap: 6,
        paddingHorizontal: 11,
      }}
    >
      <UploadCloud size={13} color={colors.greyBrown} strokeWidth={1.8} />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          fontWeight: "800",
          color: colors.greyBrown,
        }}
      >
        Upload
      </Text>
    </Pressable>
  );

  const renderPaymentRecord = (record: PaymentRecord) => {
    const tone = getPaymentToneStyle(record.status);
    const RecordIcon = getPaymentRecordIcon(record);
    const needsDocument = record.documents.some(
      (document) => document.status === "needed",
    );
    const showDocumentBadge =
      needsDocument || record.status === "needs-receipt";
    const showStatusBadge = shouldShowPaymentStatusBadge(record.status);
    const amountLabel =
      record.billedAmount > 0 || record.status === "due"
        ? formatCurrency(record.amount, locale)
        : "Pending";

    return (
      <Row
        key={record.id}
        surface="plain"
        leading={
          <RecordIcon
            size={17}
            color={PAYMENT_ROW_ICON_COLOR}
            strokeWidth={1.8}
          />
        }
        leadingSize={28}
        title={record.label}
        subtitle={`${record.provider} · ${record.date}`}
        titleRight={
          <View style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
            {showStatusBadge
              ? renderStatusBadge(tone.label, tone.badgeTone)
              : null}
            {showDocumentBadge ? renderDocumentNeededBadge() : null}
          </View>
        }
        trailing={
          <Text
            className="font-dm-mono"
            style={{
              fontSize: 13,
              fontWeight: "800",
              color: EMBER.colors.text,
              textAlign: "right",
            }}
          >
            {amountLabel}
          </Text>
        }
        onPress={() => setSelectedPaymentRecordId(record.id)}
        paddingHorizontal={16}
        paddingVertical={12}
        titleStyle={{
          fontSize: 15,
          fontWeight: "600",
          color: EMBER.colors.text,
        }}
        subtitleStyle={{
          fontSize: 12,
          color: EMBER.colors.textLight,
          marginTop: 2,
        }}
      />
    );
  };

  const renderPaymentDetails = () => (
    <View style={{ gap: 12 }}>
      <PaymentProgressBar
        segments={paymentBreakdownSegments}
        leftToPay={leftToPay}
        locale={locale}
        onPay={() => onNavigate("Financing")}
        onAdd={() => setUploadModalMode("payment")}
      />

      {paymentRecords.length === 0 ? (
        <View
          style={{
            borderRadius: 18,
            borderWidth: 1,
            borderColor: "rgba(42, 37, 34, 0.08)",
            backgroundColor: "rgba(255, 255, 255, 0.22)",
            padding: 16,
            alignItems: "center",
            gap: 10,
          }}
        >
          <Image
            source={COST_QUOTE_RECEIPT_ILLUSTRATION}
            resizeMode="contain"
            style={{ width: 82, height: 68 }}
          />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              fontWeight: "800",
              color: EMBER.colors.text,
              textAlign: "center",
            }}
          >
            No payments or claims yet
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              maxWidth: 310,
              fontSize: 12,
              lineHeight: 18,
              color: EMBER.colors.textMid,
              textAlign: "center",
            }}
          >
            Add payments, claims, receipts, EOBs, and balances as treatment
            starts.
          </Text>
        </View>
      ) : (
        <View style={{ gap: 8 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              paddingHorizontal: 4,
            }}
          >
            <SectionTitle icon={FileCheck2}>Payments & claims</SectionTitle>
            <ViewAllLink onPress={openFullPaymentsList} />
          </View>

          <View
            style={{
              borderRadius: 18,
              borderWidth: 1,
              borderColor: "rgba(42, 37, 34, 0.08)",
              backgroundColor: "rgba(255, 255, 255, 0.18)",
              overflow: "hidden",
            }}
          >
            {paymentPreviewRecords.map((record, index) => (
              <View
                key={record.id}
                style={{
                  borderTopWidth: index > 0 ? 1 : 0,
                  borderTopColor: "rgba(42, 37, 34, 0.065)",
                }}
              >
                {renderPaymentRecord(record)}
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );

  const renderPaymentRecordDetailModal = () => {
    if (!selectedPaymentRecord) return null;
    const record = selectedPaymentRecord;

    return (
      <PaymentRecordDetailModal
        record={record}
        locale={locale}
        onClose={() => setSelectedPaymentRecordId(null)}
        onAttachDocument={() => setAttachmentRecordId(record.id)}
        onSaveRecord={handlePaymentRecordSaved}
        onDeleteRecord={() => deletePaymentRecord(record.id)}
        onReviewInsurance={() => {
          setSelectedPaymentRecordId(null);
          onNavigate("Insurance");
        }}
        onOpenFinancing={() => {
          setSelectedPaymentRecordId(null);
          onNavigate("Financing");
        }}
        onOpenTaxTracker={() => {
          setSelectedPaymentRecordId(null);
          onNavigate("TaxReview", { paymentRecordId: record.id });
        }}
        onAskChat={(record) => {
          setSelectedPaymentRecordId(null);
          onOpenChat(record);
        }}
      />
    );
  };

  return (
    <View
      style={{
        ...GLASS_CARD_STYLE,
        padding: 18,
        gap: 16,
        position: "relative",
        overflow: "visible",
        zIndex: 100,
        elevation: 100,
      }}
    >
      <InfoSheet
        visible={showInfo}
        onClose={() => setShowInfo(false)}
        title="How this money card works"
        sections={ESTIMATE_INFO_SECTIONS}
      />
      <Dialog
        visible={quoteDeleteTarget != null}
        onClose={() => {
          if (!deleteClinicQuoteMutation.isPending) {
            setQuoteDeleteTargetId(null);
            setQuoteDeleteError(null);
          }
        }}
        closeButton="with-background"
        containerStyle={{ width: "90%", maxWidth: 430 }}
      >
        <DialogHeader
          title="Delete this quote?"
          subtitle={
            quoteDeleteTarget
              ? `${quoteDeleteTarget.name} will be removed from your saved quotes.`
              : "This quote will be removed from your saved quotes."
          }
          style={{ paddingRight: 40 }}
        />
        {quoteDeleteError ? (
          <Text
            className="font-dm-sans"
            style={{
              marginTop: 14,
              fontSize: 12,
              lineHeight: 17,
              color: EMBER.colors.coral,
            }}
          >
            {quoteDeleteError}
          </Text>
        ) : null}
        <DialogActions style={{ marginTop: 18 }}>
          <SoftSecondaryButton
            label="Cancel"
            onPress={() => {
              setQuoteDeleteTargetId(null);
              setQuoteDeleteError(null);
            }}
            disabled={deleteClinicQuoteMutation.isPending}
            flex={1}
          />
          <DialogPrimaryButton
            label={
              deleteClinicQuoteMutation.isPending ? "Deleting…" : "Delete quote"
            }
            onPress={deleteQuote}
            loading={deleteClinicQuoteMutation.isPending}
            flex={1}
          />
        </DialogActions>
      </Dialog>
      <CostDocumentUploadModal
        visible={uploadModalMode != null}
        mode={uploadModalMode ?? "quote"}
        onClose={() => setUploadModalMode(null)}
        onDocumentPicked={handleCostDocumentPicked}
        onManualPaymentAdded={handleManualPaymentAdded}
      />
      <CostDocumentUploadModal
        visible={attachmentRecordId != null}
        mode="payment"
        documentOnly
        onClose={() => setAttachmentRecordId(null)}
        onDocumentPicked={attachPaymentDocument}
        onManualPaymentAdded={async () => undefined}
      />
      {renderPaymentRecordDetailModal()}
      {state === "clinic-estimate" && quoteCards.length > 0
        ? renderQuoteUploadButton()
        : null}

      {actionButtonStyle === "care" && state === "estimate" ? (
        <View style={{ zIndex: 30 }}>
          {renderAssumptions({ compact: true })}
        </View>
      ) : hideInternalStateSwitch ? null : (
        <StateSwitch active={state} onChange={onStateChange} />
      )}

      {actionButtonStyle === "care" && state === "estimate" ? (
        <View
          style={{
            alignItems: "center",
            marginTop: 8,
            marginBottom: -6,
          }}
        >
          <Image
            source={COST_ESTIMATE_ILLUSTRATION}
            resizeMode="contain"
            style={{
              width: 76,
              height: 64,
              opacity: 0.82,
            }}
          />
        </View>
      ) : null}

      {state === "clinic-estimate" && quoteCards.length > 0 ? (
        <View
          style={{
            alignItems: "center",
            marginTop: 2,
            marginBottom: -2,
          }}
        >
          <Image
            source={COST_QUOTE_RECEIPT_ILLUSTRATION}
            resizeMode="contain"
            style={{ width: 82, height: 68, opacity: 0.82 }}
          />
        </View>
      ) : null}

      {showHeroSummary ? (
        <View
          style={{
            flexDirection: actionButtonStyle === "care" ? "column" : "row",
            flexWrap: actionButtonStyle === "care" ? "nowrap" : "wrap",
            alignItems: "flex-start",
            justifyContent: "space-between",
            columnGap: 18,
            rowGap: 12,
            zIndex: 10,
          }}
        >
          <View
            style={{
              flex: 1,
              minWidth: 180,
              alignItems:
                actionButtonStyle === "care" ? "center" : "flex-start",
              alignSelf: actionButtonStyle === "care" ? "stretch" : undefined,
              gap: 6,
            }}
          >
            <View
              testID="cost-estimate-total"
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent:
                  actionButtonStyle === "care" ? "center" : "flex-start",
                gap: 6,
              }}
            >
              <AnimatedCurrencyAmount
                value={summaryAmountValue}
                locale={locale}
                color={
                  actionButtonStyle === "care"
                    ? colors.greyBrown
                    : EMBER.colors.text
                }
              />
              <InfoButton onPress={() => setShowInfo(true)} />
            </View>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                color: EMBER.colors.textLight,
                textAlign: actionButtonStyle === "care" ? "center" : "left",
              }}
            >
              {summary.amountLabel}
            </Text>
            {actionButtonStyle === "care" && !showBreakdown && cardNarrative ? (
              <Text
                className="font-dm-sans"
                style={{
                  maxWidth: 360,
                  fontSize: 12,
                  lineHeight: 18,
                  color: EMBER.colors.textMid,
                  textAlign: "center",
                }}
              >
                {cardNarrative}
              </Text>
            ) : null}
          </View>
          {state === "estimate" && actionButtonStyle !== "care" ? (
            <View
              style={{
                alignItems: "flex-end",
                flexShrink: 1,
                minWidth: 0,
                paddingTop: 1,
              }}
            >
              {renderAssumptions()}
            </View>
          ) : null}
        </View>
      ) : null}

      {state === "estimate" && locationNoticeMessage ? (
        <View
          style={{
            flexDirection: "row",
            alignItems: "flex-start",
            gap: 8,
            borderRadius: 12,
            backgroundColor: "rgba(42, 37, 34, 0.05)",
            paddingHorizontal: 12,
            paddingVertical: 10,
          }}
        >
          <MapPin
            size={13}
            color={EMBER.colors.textLight}
            strokeWidth={1.8}
            style={{ marginTop: 2 }}
          />
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 12,
              lineHeight: 17,
              color: EMBER.colors.textMid,
            }}
          >
            {locationNoticeMessage}
          </Text>
        </View>
      ) : null}

      {state === "estimate" ? renderEstimateDetails() : null}
      {state === "clinic-estimate" ? renderQuoteDetails() : null}
      {state === "payments" ? renderPaymentDetails() : null}

      {financialPlanLabel ? (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <CheckCircle2
            size={14}
            color={EMBER.colors.green}
            strokeWidth={1.8}
          />
          <Text
            className="font-dm-sans"
            style={{ flex: 1, fontSize: 12, color: EMBER.colors.textLight }}
          >
            {financialPlanLabel}
          </Text>
        </View>
      ) : null}

      {actionButtonStyle === "care" ? (
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
          {summary.secondaryRoute ? (
            <CarePillActionButton
              label={summary.secondaryAction}
              icon={SecondaryActionIcon}
              variant="secondary"
              flexGrow={1}
              flexBasis={0}
              onPress={() => onNavigate(summary.secondaryRoute!)}
            />
          ) : null}
          <CarePillActionButton
            label={summary.primaryAction}
            icon={PrimaryActionIcon}
            variant="primary"
            surface="selected"
            flexGrow={1}
            flexBasis={0}
            onPress={runPrimaryAction}
          />
        </View>
      ) : (
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
          <Pressable
            onPress={() => {
              capturePress(pressLabel(summary.primaryAction));
              runPrimaryAction();
            }}
            style={{
              flex: 1,
              minWidth: 190,
              minHeight: 40,
              borderRadius: 14,
              backgroundColor: "#d6cbc3",
              alignItems: "center",
              justifyContent: "center",
              paddingHorizontal: 14,
              flexDirection: "row",
              gap: 7,
            }}
          >
            <PrimaryActionIcon
              size={15}
              color={EMBER.colors.text}
              strokeWidth={1.8}
            />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "700",
                color: EMBER.colors.text,
                textAlign: "center",
              }}
            >
              {summary.primaryAction}
            </Text>
          </Pressable>
          {summary.secondaryRoute ? (
            <Pressable
              onPress={() => {
                capturePress(pressLabel(summary.secondaryAction));
                onNavigate(summary.secondaryRoute!);
              }}
              style={{
                flex: 1,
                minWidth: 190,
                minHeight: 40,
                borderRadius: 14,
                borderWidth: 1,
                borderColor: "rgba(42, 37, 34, 0.12)",
                backgroundColor: "rgba(255, 255, 255, 0.24)",
                alignItems: "center",
                justifyContent: "center",
                paddingHorizontal: 14,
                flexDirection: "row",
                gap: 7,
              }}
            >
              <SecondaryActionIcon
                size={15}
                color={EMBER.colors.textMid}
                strokeWidth={1.8}
              />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "600",
                  color: EMBER.colors.textMid,
                  textAlign: "center",
                }}
              >
                {summary.secondaryAction}
              </Text>
            </Pressable>
          ) : null}
        </View>
      )}
    </View>
  );
}

export default function CostsOverviewConceptScreen({
  navigation,
  route,
}: Props) {
  const rootNavigation =
    navigation.getParent<NavigationProp<MainTabParamList>>();
  const region = useRegion();
  const { locale } = useLocale();
  const t = useTranslations();
  const capturePress = usePressCapture("CostsOverviewConceptScreen");
  const { userId } = useApiClient();
  const { data: onboardingData } = useOnboarding();
  const { data: patient } = usePatient();
  const authorizedExperience = useAuthorizedExperienceProjection();
  const canonicalExperience = authorizedExperience.projection?.context;
  const acceptedPlan = authorizedExperience.canonical?.acceptedTreatmentPlan;
  const {
    primaryPolicy,
    hasInsurance,
    verificationStatus,
    fertilityCoverage,
    isLoading: coverageLoading,
  } = useCoverage();
  const financials = useCoverageFinancials();
  const comparison = useClinicComparisonStore(userId);
  const [insuranceModalVisible, setInsuranceModalVisible] = useState(false);
  const [reviewStateControlBar, setReviewStateControlBar] =
    useState<ConceptCostState | null>(
      route.params?.initialState ?? getUrlCostState(),
    );
  const requestedInitialState = route.params?.initialState;
  const requestedUploadMode = route.params?.openUploadMode;

  useEffect(() => {
    if (!requestedInitialState) return;
    setReviewStateControlBar(requestedInitialState);
    navigation.setParams({ initialState: undefined });
  }, [navigation, requestedInitialState]);

  const consumeUploadMode = useCallback(() => {
    navigation.setParams({ openUploadMode: undefined });
  }, [navigation]);

  const treatmentIntent: TreatmentIntent | null = canonicalExperience?.treatment
    .hasActiveCycle
    ? "InTreatment"
    : null;
  const hasPartner =
    canonicalExperience?.familyBuildingPlan.partnerChoice === "with-partner";
  const isUsingDonor =
    canonicalExperience?.familyBuildingPlan.partnerChoice === "using-donor";
  const plannedModality = resolvePlannedModality(
    normalizePlannedModalities(
      authorizedExperience.canonical?.intent.plannedModalities,
    ),
  );
  const acceptedPlanTreatmentType = getAcceptedPlanTreatmentType(
    acceptedPlan?.treatmentFamily,
  );
  const canonicalTreatmentType =
    authorizedExperience.status === "ready"
      ? (acceptedPlanTreatmentType ??
        getCanonicalTreatmentType(
          canonicalExperience?.familyBuildingPlan.treatmentGoal,
          plannedModality,
        ))
      : null;
  // Keep the estimate and its Scenario Builder available while context loads
  // or remains exploratory. A canonical treatment path remains the initial
  // selection; the fallback keeps the existing estimate surface interactive
  // while the person chooses a path.
  const initialTreatmentType = canonicalTreatmentType ?? "ivf";
  const profileAddress = patient?.addresses?.[0];
  const profileCountryCode =
    profileAddress?.country === "USA"
      ? "US"
      : (profileAddress?.country ?? "US");
  const profileZipCode = profileAddress?.postalCode ?? onboardingData.zipCode;
  const profileCity = profileAddress?.city ?? null;
  // The onboarding state derivation is intentionally not persisted by the
  // current address contract. Resolve it from the canonical postal code so
  // Costs does not depend on cleared onboarding context after reload.
  const profileState = inferUsStateCodeFromZip(profileZipCode);
  const diagnosticProfile: DiagnosticProfile =
    canonicalExperience?.viewer.sexAtBirth === "AMAB" ||
    canonicalTreatmentType === "sperm_freezing"
      ? "male"
      : hasPartner
        ? "couple"
        : "female";

  const verifiedClinicCount = Object.values(
    comparison.verificationStatuses,
  ).filter((status) => status === "verified").length;
  const derivedState = deriveConceptCostState({
    intent: treatmentIntent,
    comparisonCount: comparison.comparisonClinicIds.length,
    verifiedClinicCount,
  });
  const controlBarActiveState = reviewStateControlBar ?? derivedState;

  const payerName =
    primaryPolicy?.payer?.displayName ??
    primaryPolicy?.insuranceCarrier ??
    financials.carrierName;
  const insuranceLabel = !hasInsurance
    ? "Not added"
    : verificationStatus === "verified"
      ? `${payerName ?? "Insurance"} verified`
      : verificationStatus === "pending"
        ? `${payerName ?? "Insurance"} pending`
        : (payerName ?? "Insurance added");
  const financialPlanLabel =
    financials.lifetimeMax || financials.annualMax
      ? `Known benefit limit: ${financials.lifetimeMax ?? financials.annualMax}`
      : financials.outOfPocketMax
        ? `Known out-of-pocket max: ${financials.outOfPocketMax}`
        : null;

  const isUS = region === "US";
  const comingSoon = t("costs.coming_soon");
  const currentFinancingProviderCount = currentFinancingProviders().length;
  const financingSubtitle =
    currentFinancingProviderCount > 0
      ? `${currentFinancingProviderCount} providers · ${
          hasCurrentPromotionalFinancing()
            ? "promotional options available"
            : "current terms vary"
        }`
      : "Current provider details unavailable";
  const reportedEmployerBenefit =
    patient?.fertilityIntent?.employerBenefit ?? onboardingData.employerBenefit;
  const insuranceSummary = !hasInsurance
    ? "Add your insurance to see coverage"
    : verificationStatus === "verified"
      ? `${primaryPolicy?.payer?.displayName ?? primaryPolicy?.insuranceCarrier ?? "Insurance"} · Verified`
      : verificationStatus === "pending"
        ? `${primaryPolicy?.insuranceCarrier ?? "Insurance"} · Verification pending`
        : `${primaryPolicy?.insuranceCarrier ?? "Insurance"}`;

  const categories: CategoryCard[] = [
    {
      title: t("costs.insurance"),
      subtitle: isUS ? insuranceSummary : comingSoon,
      icon: Shield,
      iconVariant: "mahogany",
      route: "Insurance",
      action: "costs.insurance",
    },
    {
      title: t("costs.employer_benefits"),
      subtitle: isUS
        ? reportedEmployerBenefit
          ? reportedEmployerBenefit
          : "Employer programs · HSA/FSA · State mandates"
        : comingSoon,
      icon: Briefcase,
      iconVariant: "copper",
      route: "EmployerBenefits",
      action: "costs.employer_benefits",
    },
    {
      title: t("costs.financing"),
      subtitle: isUS ? financingSubtitle : comingSoon,
      icon: CreditCard,
      iconVariant: "amber",
      route: "Financing",
      action: "costs.financing",
    },
    {
      title: t("costs.grants"),
      subtitle: isUS ? `${GRANTS.length} programs available` : comingSoon,
      icon: Gift,
      iconVariant: "bronze",
      route: "Grants",
      action: "costs.grants",
    },
    {
      title: t("costs.tax_deductions"),
      subtitle: isUS ? "Fertility expenses may be deductible" : comingSoon,
      icon: Receipt,
      iconVariant: "ember",
      route: "TaxDeductions",
      action: "costs.tax_deductions",
    },
  ];

  const openInsuranceModal = () => setInsuranceModalVisible(true);
  const navigateToRoute = (
    route: CostsOverviewRouteName,
    params?: CostsOverviewRouteParams,
  ) => {
    switch (route) {
      case "Insurance":
        navigation.navigate("Insurance");
        break;
      case "EmployerBenefits":
        navigation.navigate("EmployerBenefits");
        break;
      case "Financing":
        navigation.navigate("Financing");
        break;
      case "Grants":
        navigation.navigate("Grants");
        break;
      case "Payments":
        navigation.navigate("Payments");
        break;
      case "TaxDeductions":
        navigation.navigate("TaxDeductions");
        break;
      case "TaxReview":
        navigation.navigate("TaxReview", params);
        break;
      case "Glossary":
        navigation.navigate("Glossary");
        break;
    }
  };

  return (
    <GlassScreenLayout
      overlay={
        <InsuranceInfoModal
          visible={insuranceModalVisible}
          onClose={() => setInsuranceModalVisible(false)}
          primaryPolicy={primaryPolicy}
          hasInsurance={hasInsurance}
          verificationStatus={verificationStatus}
          fertilityCoverage={fertilityCoverage}
          isLoading={coverageLoading}
        />
      }
    >
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP || HEADER_INSET,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <ScreenHeader title={t("costs.costs")} />
        {isUS ? (
          <View
            style={{
              zIndex: 100,
              elevation: 100,
              overflow: "visible",
              gap: 16,
            }}
          >
            <View style={{ zIndex: 300, elevation: 300, overflow: "visible" }}>
              <PhysicalStateTabs
                active={controlBarActiveState}
                onChange={setReviewStateControlBar}
              />
              <AdaptiveCostStatusCard
                state={controlBarActiveState}
                onStateChange={setReviewStateControlBar}
                initialTreatmentType={initialTreatmentType}
                diagnosticProfile={diagnosticProfile}
                locale={locale}
                profileCity={profileCity}
                profileState={profileState}
                profileZipCode={profileZipCode}
                profileCountryCode={profileCountryCode}
                hasInsurance={hasInsurance}
                coverageVerified={verificationStatus === "verified"}
                verificationStatus={verificationStatus}
                insuranceLabel={insuranceLabel}
                fertilityCoverage={fertilityCoverage}
                primaryPolicy={primaryPolicy ?? null}
                clinicCount={comparison.comparisonClinicIds.length}
                verifiedClinicCount={verifiedClinicCount}
                financialPlanLabel={financialPlanLabel}
                onAddInsurance={openInsuranceModal}
                onNavigate={navigateToRoute}
                onViewAllPayments={(params) =>
                  navigation.navigate("Payments", params)
                }
                onOpenChat={(record) => {
                  rootNavigation?.navigate("Chat", {
                    screen: "Assistant",
                    params: {
                      initialPrompt: buildPaymentRecordChatPrompt(
                        record,
                        locale,
                      ),
                      autoSendInitialPrompt: true,
                    },
                  });
                }}
                onCompareQuotes={(context) => {
                  rootNavigation?.navigate("Chat", {
                    screen: "Assistant",
                    params: {
                      initialPrompt: buildQuoteComparisonChatPrompt(
                        context,
                        locale,
                      ),
                      autoSendInitialPrompt: true,
                    },
                  });
                }}
                initialUploadMode={requestedUploadMode}
                onUploadModeConsumed={consumeUploadMode}
                actionButtonStyle="care"
                topControlsVariant="bar"
                hideInternalStateSwitch
              />
            </View>
          </View>
        ) : (
          <View style={{ ...GLASS_CARD_STYLE, padding: 20, gap: 12 }}>
            <Building2
              size={20}
              color={EMBER.colors.textLight}
              strokeWidth={1.5}
            />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 20,
                fontWeight: "700",
                color: EMBER.colors.text,
              }}
            >
              {t("costs.us_first_title")}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                color: EMBER.colors.textMid,
                lineHeight: 21,
              }}
            >
              {t("costs.us_first_body")}
            </Text>
          </View>
        )}

        {isUsingDonor && (
          <View
            style={{
              marginTop: 12,
              padding: 14,
              borderRadius: 12,
              backgroundColor: "rgba(255, 255, 255, 0.45)",
              borderWidth: 1,
              borderColor: "rgba(42, 37, 34, 0.08)",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                color: EMBER.colors.textMid,
                lineHeight: 19,
              }}
            >
              {t("costs.donor_scenario_note")}
            </Text>
          </View>
        )}

        <View style={{ marginTop: 24, marginBottom: 12, paddingHorizontal: 2 }}>
          <SectionTitle icon={Wallet}>
            {t("costs.coverage_and_ways_to_pay")}
          </SectionTitle>
        </View>

        <View style={{ gap: 12 }}>
          {categories.map((cat) => {
            const isCategoryComing = cat.subtitle === comingSoon;
            return (
              <Pressable
                key={cat.route}
                onPress={
                  isCategoryComing
                    ? undefined
                    : () => {
                        // The subtitle carries the person's carrier and plan
                        // details, so only the category title is captured.
                        capturePress(pressLabel(cat.title), cat.action);
                        navigateToRoute(cat.route);
                      }
                }
                style={{
                  ...(GLASS_CARD_STYLE as object),
                  padding: 18,
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 14,
                  opacity: isCategoryComing ? 0.5 : 1,
                }}
              >
                <GradientIcon
                  icon={cat.icon}
                  variant={cat.iconVariant}
                  size={24}
                  strokeWidth={1.6}
                />
                <View style={{ flex: 1, gap: 2 }}>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 16,
                      fontWeight: "600",
                      color: EMBER.colors.text,
                    }}
                  >
                    {cat.title}
                  </Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      color: EMBER.colors.textLight,
                      lineHeight: 18,
                    }}
                  >
                    {cat.subtitle}
                  </Text>
                </View>
                <ChevronRight
                  size={16}
                  color={EMBER.colors.textFaint}
                  strokeWidth={1.5}
                />
              </Pressable>
            );
          })}
        </View>

        <View style={{ marginTop: 16 }}>
          <CompareClinicCostsCard />
        </View>

        <RegionGate regions={["US"]}>
          <View style={{ marginTop: 24 }}>
            <InsuranceGlossaryCard
              onPress={() => navigation.navigate("Glossary")}
            />
          </View>
        </RegionGate>

        <View style={{ marginTop: 24 }}>
          <FaqSection />
        </View>

        <View style={{ marginTop: 16 }}>
          <ChatWithUsCard />
        </View>
      </ScrollView>
    </GlassScreenLayout>
  );
}
